import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import '../styles/chat.css';
import NewConversationModal from '../components/NewConversationModal';
import GroupSettingsModal from '../components/GroupSettingsModal';
import { PlayerAvatar, usePrefetchAvatars } from '../components/PlayerAvatar';
import ClickablePseudo from '../components/ClickablePseudo';
import { GlobalChatIcon, NewChatIcon, DisconnectedIcon, MessageStatusIcon } from '../components/Icons';
import ChatMessageShell from '../components/chat/ChatMessageShell';
import MessageQuoteBlock from '../components/chat/MessageQuoteBlock';
import MessageReactions from '../components/chat/MessageReactions';
import ChatInputComposer from '../components/chat/ChatInputComposer';
import GameInviteMessage from '../components/chat/GameInviteMessage';
import LibraryShareMessage from '../components/chat/LibraryShareMessage';
import CiaAgendaInviteMessage from '../components/chat/CiaAgendaInviteMessage';
import CiaRdvModal from '../components/competitions/CiaRdvModal';
import GameInviteOfflineConfirm from '../components/chat/GameInviteOfflineConfirm';
import GameInviteExistingConfirm from '../components/chat/GameInviteExistingConfirm';
import GlobalChatFeed from '../components/chat/GlobalChatFeed';
import ChatMessageContent from '../components/chat/ChatMessageContent';
import type { MessageQuote, MessageReaction, ReplyTarget, GameInvitePayload, LibrarySharePayload, CiaAgendaInvitePayload } from '../types/chat';
import { formatChatMessagePreview, parseChatDeepLinks } from '../utils/chatDeepLinks';
import { useApp } from '../contexts/AppContext';
import { dispatchLibraryNewShare } from '../hooks/useLibraryNotifications';
import MasterDetail from '../components/MasterDetail';
import { useResponsive } from '../hooks/useResponsive';
import { useTabReselect } from '../hooks/useTabReselect';
import type { Session } from '../types/app';
import { BACKEND_URL } from '../config';
import { isBotPseudo } from '../utils/pseudo';
import { useSystemStatusContext } from '../contexts/SystemStatusContext';
import { shouldShowBetaUI } from '../components/BetaWelcomeModal';

const BETA_GLOBAL_ROOM = 'global:beta';

// Types (Session importé depuis types/app)

interface ChatMessage {
  roomId: string;
  pseudo: string;
  text: string;
  timestamp: number;
  source?: 'app' | 'irc';
  messageId?: string;
  delivered?: boolean;
  quote?: MessageQuote;
  replyToMessageId?: string;
  reactions?: MessageReaction[];
  messageType?: 'text' | 'game_invite';
  gameInvite?: GameInvitePayload;
}

interface ConnectedUser {
  pseudo: string;
  pseudoDisplay: string;
  source: 'app' | 'irc';
  lastSeen: number;
  team?: string;  // Nom de l'équipe (mode compétition)
}

interface SimulationStatus {
  active: boolean;
  interval: number;
  connectedUsers: number;
  available?: boolean;
  godMode?: boolean;
}

interface Conversation {
  id: string;
  type: 'direct' | 'group' | 'legacy'; // legacy = messages du site webtarot.fr
  name?: string;
  displayName?: string;   // Nom affiché (peut différer selon le viewer)
  avatarEmoji?: string;
  avatarColor?: string;
  avatarUrl?: string;     // URL de l'avatar (photo d'équipe par ex.)
  participants: string[];
  unreadCount: number;
  pinned?: boolean;
  isTeamGroup?: boolean;  // Groupe d'équipe (synchronisé automatiquement)
  teamId?: number;        // ID de l'équipe webtarot
  isInterTeam?: boolean;  // Groupe inter-équipes
  teamAId?: number;       // ID de l'équipe A (créateur)
  teamBId?: number;       // ID de l'équipe B (sélectionnée)
  teamAName?: string;     // Nom de l'équipe A
  teamBName?: string;     // Nom de l'équipe B
  teamAMembers?: string[]; // Membres de l'équipe A
  teamBMembers?: string[]; // Membres de l'équipe B
  userTeamId?: number;    // ID de l'équipe de l'utilisateur actuel
  otherTeamId?: number;   // ID de l'autre équipe (pour affichage)
  otherTeamName?: string; // Nom de l'autre équipe
  lastMessage?: {
    text: string;
    author: string;
    timestamp: string;
    status?: 'sent' | 'delivered' | 'read';
  };
  updatedAt: string;
  // Propriétés legacy
  isLegacy?: boolean;           // true = conversation du site legacy
  legacyCorrespondant?: string; // Pseudo encodé du correspondant
  totalMessages?: number;       // Nombre total de messages legacy
}

function isOneToOneConversation(conv: Conversation | null | undefined): boolean {
  return conv?.type === 'direct' || conv?.type === 'legacy';
}

// Note: Le statut des messages est maintenant géré par le composant MessageStatusIcon dans Icons.tsx

// Helper pour trier les conversations (épinglées d'abord, puis par date du dernier message)
function sortConversations(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort((a, b) => {
    // Les épinglées en premier
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    
    // Puis par date du dernier message (plus récent en premier)
    const dateA = a.lastMessage?.timestamp || a.updatedAt;
    const dateB = b.lastMessage?.timestamp || b.updatedAt;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });
}

// Helper pour afficher les participants d'un groupe (ex: "Alice, Bob et vous")
function getGroupParticipantsDisplay(participants: string[], currentUserPseudo: string): string {
  // Filtrer l'utilisateur actuel et trier par ordre alphabétique (insensible à la casse)
  const others = participants
    .filter(p => p.toLowerCase() !== currentUserPseudo.toLowerCase())
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  
  if (others.length === 0) {
    return 'Vous êtes seul dans ce groupe';
  }
  
  if (others.length === 1) {
    return `${others[0]} et vous`;
  }
  
  // "Alice, Bob et vous" ou "Alice, Bob, Charlie et vous"
  return `${others.join(', ')} et vous`;
}

function getTeamMembersDisplay(participants: string[], currentUserPseudo: string): string {
  const others = participants
    .filter(p => p.toLowerCase() !== currentUserPseudo.toLowerCase())
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  
  if (others.length === 0) {
    return 'Équipe (vous uniquement)';
  }
  
  if (others.length === 1) {
    return `Équipe de ${others[0]} et vous`;
  }
  
  // "Équipe de Alice, Bob et vous"
  const lastPseudo = others.pop();
  return `Équipe de ${others.join(', ')} et ${lastPseudo} et vous`;
}

// Helper pour afficher les joueurs connectés au chat global
function getGlobalChatParticipantsDisplay(connectedUsers: ConnectedUser[], currentUserPseudo: string): string {
  const totalCount = connectedUsers.length;
  
  // Si plus de 5 joueurs, afficher juste le nombre
  if (totalCount > 5) {
    return `${totalCount} joueurs connectés au chat`;
  }
  
  // Sinon, lister les pseudos
  const others = connectedUsers
    .filter(u => u.pseudo.toLowerCase() !== currentUserPseudo.toLowerCase())
    .map(u => u.pseudo)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  
  if (others.length === 0) return 'Vous seul';
  if (others.length === 1) return `${others[0]} et vous`;
  
  // Liste naturelle en français : "Alice, Bob, Charlie et vous"
  const lastPseudo = others.pop()!;
  return `${others.join(', ')}, ${lastPseudo} et vous`;
}

// Calcule les membres connectés d'un groupe d'équipe (exclut l'utilisateur)
function getConnectedTeammates(
  participants: string[], 
  currentUserPseudo: string, 
  connectedUsers: ConnectedUser[]
): string[] {
  const connectedPseudos = new Set(connectedUsers.map(u => u.pseudo.toLowerCase()));
  return participants.filter(p => 
    p.toLowerCase() !== currentUserPseudo.toLowerCase() && 
    !isBotPseudo(p) &&
    connectedPseudos.has(p.toLowerCase())
  );
}

function formatOtherParticipantNames(participants: string[], currentUserPseudo: string): string {
  const others = participants
    .filter(p => p.toLowerCase() !== currentUserPseudo.toLowerCase() && !isBotPseudo(p))
    .sort((a, b) => a.localeCompare(b, 'fr'));
  if (others.length === 0) return '';
  if (others.length === 1) return others[0];
  const last = others.pop()!;
  return `${others.join(', ')} et ${last}`;
}

interface DirectMessage {
  id: string;
  conversationId: string;
  author: string;
  text: string;
  createdAt: string;
  status: 'sent' | 'delivered' | 'read';
  messageType?: 'text' | 'game_invite' | 'library_share' | 'cia_agenda_invite';
  gameInvite?: GameInvitePayload;
  libraryShare?: LibrarySharePayload;
  ciaAgendaInvite?: CiaAgendaInvitePayload;
  isLegacy?: boolean;
  titre?: string;
  quote?: MessageQuote;
  reactions?: MessageReaction[];
}

interface ChatProps {
  session: Session;
  isAdmin?: boolean;
  canModerate?: boolean;
}

// Helper pour formater l'heure (conversations privées)
function formatTime(timestamp: number | string | Date): string {
  let date: Date;
  
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    // Essayer de parser la date ISO ou autre format
    date = new Date(timestamp);
  } else {
    date = timestamp;
  }
  
  // Vérifier si la date est valide
  if (isNaN(date.getTime())) {
    return '--:--';
  }
  
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Helper pour formater la date intelligemment (pour la liste des conversations)
function formatConversationDate(timestamp: number | string | Date): string {
  let date: Date;
  
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    date = timestamp;
  }
  
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - messageDay.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Aujourd'hui : afficher l'heure
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'hier';
  } else if (diffDays === 2) {
    return 'avant-hier';
  } else if (diffDays < 7) {
    // Jour de la semaine
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    return days[date.getDay()];
  } else {
    // Date au format JJ/MM/AA
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  }
}

// Helper pour formater la date du séparateur dans les fils de messages
function formatDateSeparator(timestamp: number | string | Date): string {
  let date: Date;
  
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    date = timestamp;
  }
  
  if (isNaN(date.getTime())) {
    return '';
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - messageDay.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return "Aujourd'hui";
  } else if (diffDays === 1) {
    return 'Hier';
  } else if (diffDays < 7) {
    // Jour de la semaine avec majuscule
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[date.getDay()];
  } else {
    // Date complète JJ/MM/AAAA
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}

// Helper pour obtenir la clé de date (YYYY-MM-DD) d'un timestamp
function getDateKey(timestamp: number | string | Date): string {
  let date: Date;
  if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    date = timestamp;
  }
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

export default function Chat({ session, isAdmin = false, canModerate = false }: ChatProps) {
  const {
    setChatUnreadCount, activeTab, setActiveTab, showToast, preferences, gameInfo,
    setConnectedUsers: setGlobalConnectedUsers,
    setGlobalMessages, setSendGlobalMessage, setChatConnected, setGlobalChatPanel,
    setPlayerTeams: setGlobalPlayerTeams,
    navigateToLibrary,
    navigateToDiffere,
  } = useApp();
  const { status } = useSystemStatusContext();
  const showBetaChat = shouldShowBetaUI(status, isAdmin);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [betaMessages, setBetaMessages] = useState<ChatMessage[]>([]);
  const [globalReactions, setGlobalReactions] = useState<Record<string, MessageReaction[]>>({});
  const [betaReactions, setBetaReactions] = useState<Record<string, MessageReaction[]>>({});
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [betaConnectedUsers, setBetaConnectedUsers] = useState<ConnectedUser[]>([]);
  const [inputText, setInputText] = useState('');
  const [replyingTo, setReplyingTo] = useState<ReplyTarget | null>(null);
  const [showUsers, setShowUsers] = useState(true);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [simulationStatus, setSimulationStatus] = useState<SimulationStatus | null>(null);
  
  // Conversations privées
  // Restaurer depuis le cache sessionStorage pour un affichage instantané lors du retour sur l'onglet
  const conversationsCacheKey = `wt_conversations_${session.pseudo.toLowerCase()}`;
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    try {
      const cached = sessionStorage.getItem(conversationsCacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch { /* cache invalide, on repart à zéro */ }
    return [];
  });
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [directMessages, setDirectMessages] = useState<DirectMessage[]>([]);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [showOfflineInviteConfirm, setShowOfflineInviteConfirm] = useState(false);
  const [showExistingInviteConfirm, setShowExistingInviteConfirm] = useState(false);
  const [ciaRdvPhaseId, setCiaRdvPhaseId] = useState<number | null>(null);
  const [ciaRdvOpponent, setCiaRdvOpponent] = useState<{ pseudo: string; display: string } | null>(null);
  const [ciaRdvChecking, setCiaRdvChecking] = useState(false);
  const [showCiaRdvModal, setShowCiaRdvModal] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [activeView, setActiveView] = useState<'global' | 'beta' | 'direct'>('global');
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [showArchivedConversations, setShowArchivedConversations] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  
  // Cache des équipes des joueurs (mode compétition)
  const [playerTeams, setPlayerTeams] = useState<Record<string, string | null>>({});
  // ID de l'équipe de l'utilisateur (mode compétition)
  const userTeamIdCacheKey = `wt_userTeamId_${session.pseudo.toLowerCase()}`;
  const [userTeamId, setUserTeamId] = useState<number | null>(() => {
    try {
      const cached = sessionStorage.getItem(userTeamIdCacheKey);
      if (cached) return JSON.parse(cached);
    } catch { /* cache invalide */ }
    return null;
  });
  
  // Indicateur de frappe (qui est en train d'écrire)
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingEmitRef = useRef<number>(0);

  // Emoji picker géré par ChatInputComposer

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Refs pour accéder aux valeurs dans les callbacks socket (évite les closures stales)
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const standardGlobalRoomId = `global:${session.compet ? 'compet' : 'detente'}`;
  const activeGlobalRoomId = activeView === 'beta' ? BETA_GLOBAL_ROOM : standardGlobalRoomId;
  const isGlobalSalonView = activeView === 'global' || activeView === 'beta';
  const salonMessages = activeView === 'beta' ? betaMessages : messages;
  const salonConnectedUsers = activeView === 'beta' ? betaConnectedUsers : connectedUsers;
  const salonReactions = activeView === 'beta' ? betaReactions : globalReactions;
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;

  // Extraire les pseudos des conversations directes pour le préchargement des avatars
  const directConversationPseudos = useMemo(() => {
    return conversations
      .filter(c => c.type === 'direct')
      .map(c => c.participants.find(p => p.toLowerCase() !== session.pseudo.toLowerCase()))
      .filter((p): p is string => !!p);
  }, [conversations, session.pseudo]);

  // Précharger les avatars des joueurs
  usePrefetchAvatars(directConversationPseudos);

  // Charger les équipes des joueurs (mode compétition)
  useEffect(() => {
    if (!session.compet) return;

    // Collecter tous les pseudos dont on a besoin des équipes
    const pseudosToFetch = new Set<string>();
    
    // Joueurs connectés
    connectedUsers.forEach(u => {
      if (!playerTeams[u.pseudo.toLowerCase()]) {
        pseudosToFetch.add(u.pseudo);
      }
    });
    
    // Participant de la conversation active (si 1-1)
    if (activeConversation?.type === 'direct') {
      const otherPseudo = activeConversation.participants.find(
        p => p.toLowerCase() !== session.pseudo.toLowerCase()
      );
      if (otherPseudo && !playerTeams[otherPseudo.toLowerCase()]) {
        pseudosToFetch.add(otherPseudo);
      }
    }

    if (pseudosToFetch.size === 0) return;

    // Récupérer les équipes pour chaque pseudo
    const fetchTeams = async () => {
      const newTeams: Record<string, string | null> = {};
      
      for (const pseudo of pseudosToFetch) {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(pseudo)}&limit=1&compet=true`
          );
          if (response.ok) {
            const data = await response.json();
            const player = data.players?.find(
              (p: { pseudo: string }) => p.pseudo.toLowerCase() === pseudo.toLowerCase()
            );
            newTeams[pseudo.toLowerCase()] = player?.team?.nom || null;
          } else {
            newTeams[pseudo.toLowerCase()] = null;
          }
        } catch {
          newTeams[pseudo.toLowerCase()] = null;
        }
      }
      
      if (Object.keys(newTeams).length > 0) {
        setPlayerTeams(prev => ({ ...prev, ...newTeams }));
      }
    };

    fetchTeams();
  }, [session.compet, connectedUsers, activeConversation, playerTeams, session.pseudo]);

  // Synchroniser le compteur de messages non lus vers le contexte global
  useEffect(() => {
    setChatUnreadCount(totalUnreadCount);
  }, [totalUnreadCount, setChatUnreadCount]);

  // Synchroniser les messages globaux vers le contexte (pour la page Home)
  useEffect(() => {
    setGlobalMessages(messages);
  }, [messages, setGlobalMessages]);

  // Synchroniser l'état de connexion chat vers le contexte
  useEffect(() => {
    setChatConnected(connected);
  }, [connected, setChatConnected]);

  // Synchroniser le cache des équipes vers le contexte
  useEffect(() => {
    setGlobalPlayerTeams(playerTeams);
  }, [playerTeams, setGlobalPlayerTeams]);

  // Connexion Socket.IO
  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('[Socket] Connecté');
      setConnected(true);
      newSocket.emit('join_global', {
        pseudo: session.pseudo,
        compet: session.compet,
      });
    });

    newSocket.on('disconnect', () => {
      console.log('[Socket] Déconnecté');
      setConnected(false);
    });

    newSocket.on('chat_history', (data: { roomId: string; history: ChatMessage[] }) => {
      if (data.roomId === BETA_GLOBAL_ROOM) {
        setBetaMessages(data.history);
      } else {
        setMessages(data.history);
      }
    });

    newSocket.on('chat_reactions', (data: { roomId: string; reactions: Record<string, MessageReaction[]> }) => {
      if (data.roomId === BETA_GLOBAL_ROOM) {
        setBetaReactions(data.reactions);
      } else if (data.roomId === standardGlobalRoomId) {
        setGlobalReactions(data.reactions);
      }
    });

    newSocket.on('message_reaction', (data: {
      roomId?: string;
      conversationId?: string;
      messageId: string;
      reactions: MessageReaction[];
    }) => {
      if (data.roomId === BETA_GLOBAL_ROOM) {
        setBetaReactions((prev) => ({
          ...prev,
          [data.messageId]: data.reactions,
        }));
      } else if (data.roomId === standardGlobalRoomId) {
        setGlobalReactions((prev) => ({
          ...prev,
          [data.messageId]: data.reactions,
        }));
      }
    });

    newSocket.on('chat_message', (message: ChatMessage) => {
      const isBetaMessage = message.roomId === BETA_GLOBAL_ROOM;
      const appendMessage = isBetaMessage ? setBetaMessages : setMessages;

      appendMessage((prev) => {
        if (prev.some((m) => m.messageId === message.messageId)) {
          return prev;
        }
        return [...prev, message];
      });

      if (
        preferencesRef.current.notifyGlobalChat &&
        message.pseudo.toLowerCase() !== session.pseudo.toLowerCase() &&
        message.messageType !== 'game_invite'
      ) {
        const notifyBeta = isBetaMessage && activeViewRef.current !== 'beta';
        const notifyGlobal = !isBetaMessage && activeViewRef.current !== 'global';
        if (notifyBeta || notifyGlobal) {
          const truncated = message.text.length > 50
            ? message.text.slice(0, 47) + '…'
            : message.text;
          showToast({
            message: `${message.pseudo} dans ${isBetaMessage ? 'le salon bêta' : 'le salon'}`,
            subtitle: truncated,
            type: 'chat',
            targetTab: 'chat',
            duration: 4000,
          }, { sourceTab: 'chat' });
        }
      }
    });

    newSocket.on('global_game_invite_updated', (data: {
      roomId: string;
      messageId: string;
      message: ChatMessage;
    }) => {
      if (data.roomId === BETA_GLOBAL_ROOM || data.roomId !== standardGlobalRoomId) return;
      setMessages((prev) => prev.map((m) => {
        const id = m.messageId || `${m.timestamp}-${m.pseudo}`;
        if (id !== data.messageId) return m;
        return {
          ...m,
          text: data.message.text,
          gameInvite: data.message.gameInvite,
          messageType: 'game_invite',
        };
      }));
    });

    newSocket.on('message_delivered', (data: { messageId: string }) => {
      const markDelivered = (prev: ChatMessage[]) =>
        prev.map((m) =>
          m.messageId === data.messageId ? { ...m, delivered: true } : m
        );
      setMessages(markDelivered);
      setBetaMessages(markDelivered);
    });

    newSocket.on('moderation:notice', (notice: { message?: string; code?: string }) => {
      showToast({
        message: notice.message || 'Action de modération',
        type: 'info',
        duration: 8000,
      });
    });

    newSocket.on('message_rejected', (data: { message?: string }) => {
      showToast({
        message: data.message || 'Message refusé',
        type: 'info',
        duration: 6000,
      });
    });

    newSocket.on('moderation:message_removed', (data: { messageId?: string; roomId?: string }) => {
      if (!data.messageId) return;
      const redact = (prev: ChatMessage[]) =>
        prev.map((m) =>
          m.messageId === data.messageId ? { ...m, text: '[message modéré]' } : m,
        );
      if (data.roomId === BETA_GLOBAL_ROOM) {
        setBetaMessages(redact);
      } else {
        setMessages(redact);
      }
    });

    newSocket.on('moderation:message_restored', (data: {
      messageId?: string;
      roomId?: string;
      text?: string;
    }) => {
      if (!data.messageId || data.text == null) return;
      const restore = (prev: ChatMessage[]) =>
        prev.map((m) =>
          m.messageId === data.messageId ? { ...m, text: data.text! } : m,
        );
      if (data.roomId === BETA_GLOBAL_ROOM) {
        setBetaMessages(restore);
      } else {
        setMessages(restore);
      }
    });

    newSocket.on('moderation:join_denied', (data: { message?: string }) => {
      showToast({
        message: data.message || 'Accès au chat refusé',
        type: 'info',
        duration: 8000,
      });
    });

    newSocket.on('connected_users', (data: ConnectedUser[] | { roomId: string; users: ConnectedUser[] }) => {
      const users = Array.isArray(data) ? data : data.users;
      const payloadRoomId = Array.isArray(data) ? standardGlobalRoomId : data.roomId;
      if (payloadRoomId === BETA_GLOBAL_ROOM) {
        setBetaConnectedUsers(users);
      } else {
        setConnectedUsers(users);
        setGlobalConnectedUsers(users);
      }
    });

    newSocket.on('user_joined', (data: { pseudo: string }) => {
      console.log(`[Socket] ${data.pseudo} a rejoint`);
    });

    newSocket.on('user_left', (data: { pseudo: string }) => {
      console.log(`[Socket] ${data.pseudo} est parti`);
    });

    newSocket.on('beep_notification', (data: { author: string; target: string }) => {
      if (data.target.toLowerCase() === session.pseudo.toLowerCase()) {
        // TODO: Notification sonore/visuelle
        alert(`🔔 ${data.author} vous fait un beep !`);
      }
    });

    // Simulation (dev only)
    newSocket.on('simulation_status', (status: SimulationStatus) => {
      setSimulationStatus(status);
    });

    newSocket.on('simulation_reset_complete', () => {
      setMessages([]);
      setBetaMessages([]);
      setGlobalReactions({});
      setBetaReactions({});
    });

    newSocket.on('conversations_reset', () => {
      console.log('[Socket] Toutes les conversations ont été réinitialisées');
      setConversations([]);
      setActiveConversation(null);
      setDirectMessages([]);
      setActiveView('global');
    });

    // Indicateur de frappe
    newSocket.on('typing_start', (data: { conversationId: string; pseudo: string }) => {
      if (data.pseudo.toLowerCase() !== session.pseudo.toLowerCase()) {
        setTypingUsers(prev => {
          if (!prev.includes(data.pseudo)) {
            return [...prev, data.pseudo];
          }
          return prev;
        });
      }
    });

    newSocket.on('typing_stop', (data: { conversationId: string; pseudo: string }) => {
      setTypingUsers(prev => prev.filter(p => p.toLowerCase() !== data.pseudo.toLowerCase()));
    });

    // Enregistrer l'utilisateur pour la simulation
    newSocket.emit('simulation_register', session.pseudo);

    // Charger le statut dev depuis l'API
    fetch(`${BACKEND_URL}/api/dev/simulation`)
      .then(res => res.json())
      .then(data => {
        if (data.available) {
          setSimulationStatus(data);
        }
      })
      .catch(() => {/* ignore */});

    setSocket(newSocket);

    // Heartbeat every 30s to keep Redis presence alive (60s timeout)
    const heartbeatInterval = setInterval(() => {
      if (newSocket.connected) {
        newSocket.emit('presence_heartbeat');
      }
    }, 30_000);

    return () => {
      clearInterval(heartbeatInterval);
      newSocket.emit('leave_global');
      newSocket.close();
    };
  }, [session.pseudo, session.compet, standardGlobalRoomId, showToast]);

  // Auto-scroll vers le bas quand nouveaux messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, betaMessages, activeView]);

  // Auto-scroll vers le bas quand l'indicateur de frappe apparaît
  useEffect(() => {
    if (typingUsers.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [typingUsers]);

  // Envoyer un message
  const sendMessage = useCallback(() => {
    if (!socket || !inputText.trim()) return;

    const message: ChatMessage = {
      roomId: activeGlobalRoomId,
      pseudo: session.pseudo,
      text: inputText.trim(),
      timestamp: Date.now(),
      ...(replyingTo ? { replyToMessageId: replyingTo.id } : {}),
    };

    socket.emit('chat_message', message);
    setInputText('');
    setReplyingTo(null);
    inputRef.current?.focus();
  }, [socket, inputText, activeGlobalRoomId, session.pseudo, replyingTo]);

  // Exposer une fonction d'envoi de message global via le contexte (pour la page Home)
  useEffect(() => {
    if (!socket) {
      setSendGlobalMessage(null);
      return;
    }
    const sendFn = (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      socket.emit('chat_message', {
        roomId: `global:${session.compet ? 'compet' : 'detente'}`,
        pseudo: session.pseudo,
        text: trimmed,
        timestamp: Date.now(),
      });
    };
    setSendGlobalMessage(sendFn);
    return () => setSendGlobalMessage(null);
  }, [socket, session.pseudo, session.compet, setSendGlobalMessage]);

  // Contrôles de simulation
  const toggleSimulation = useCallback(() => {
    if (!socket) return;
    socket.emit('simulation_toggle', !simulationStatus?.active);
  }, [socket, simulationStatus]);

  const resetSimulation = useCallback(() => {
    if (!socket) return;
    if (confirm('Êtes-vous sûr de vouloir réinitialiser toutes les discussions ?')) {
      socket.emit('simulation_reset');
    }
  }, [socket]);

  // ==========================================
  // Conversations privées
  // ==========================================

  // Sauvegarder les conversations en sessionStorage pour un affichage instantané
  // lors des changements d'onglet (Discussions ↔ Tables)
  useEffect(() => {
    if (conversations.length > 0 && !showArchivedConversations) {
      try {
        sessionStorage.setItem(conversationsCacheKey, JSON.stringify(conversations));
      } catch { /* sessionStorage plein ou indisponible */ }
    }
  }, [conversations, conversationsCacheKey, showArchivedConversations]);

  useEffect(() => {
    if (userTeamId !== null) {
      try {
        sessionStorage.setItem(userTeamIdCacheKey, JSON.stringify(userTeamId));
      } catch { /* sessionStorage plein ou indisponible */ }
    }
  }, [userTeamId, userTeamIdCacheKey]);

  // Garder une trace des compteurs locaux de non-lus (plus fiable que le serveur)
  // Initialiser depuis le cache si disponible
  const localUnreadCountsRef = useRef<Map<string, number>>(
    new Map(conversations.map(c => [c.id, c.unreadCount || 0]))
  );

  // Charger les conversations au démarrage (une seule fois)
  // Si le cache a fourni des conversations, on considère qu'elles sont "chargées"
  // pour que le fetch suivant utilise le chemin de fusion (merge) au lieu du remplacement total
  const conversationsLoadedRef = useRef(conversations.length > 0);
  
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const archivedParam = showArchivedConversations ? '&archived=true' : '';
        
        // Charger les conversations ET les données d'équipe en parallèle
        // pour éviter un flash visuel lors de la mise à jour des métadonnées d'équipe
        const fetchPromises: Promise<Response | null>[] = [
          fetch(`${BACKEND_URL}/api/conversations?pseudo=${encodeURIComponent(session.pseudo)}${archivedParam}`),
        ];
        
        if (session.compet) {
          fetchPromises.push(
            fetch(`${BACKEND_URL}/api/conversations/team-group?pseudo=${encodeURIComponent(session.pseudo)}`).catch(() => null),
            fetch(`${BACKEND_URL}/api/conversations/inter-team?pseudo=${encodeURIComponent(session.pseudo)}`).catch(() => null),
          );
        }
        
        const responses = await Promise.all(fetchPromises);
        const conversationsResponse = responses[0];
        const teamResponse = session.compet ? responses[1] : null;
        const interTeamResponse = session.compet ? responses[2] : null;
        
        // 1. Traiter les conversations générales
        let serverConvs: Conversation[] = [];
        if (conversationsResponse && conversationsResponse.ok) {
          const data = await conversationsResponse.json();
          serverConvs = data.conversations || [];
          
          if (!showArchivedConversations && data.archivedCount !== undefined) {
            setArchivedCount(data.archivedCount);
          }
        }
        
        // 2. Traiter le groupe d'équipe
        let teamGroup: Conversation | null = null;
        if (teamResponse && teamResponse.ok) {
          const data = await teamResponse.json();
          teamGroup = data.teamGroup || null;
          if (teamGroup?.teamId) {
            setUserTeamId(teamGroup.teamId);
          }
        }
        
        // 3. Traiter les groupes inter-équipes
        let interTeamGroups: Conversation[] = [];
        if (interTeamResponse && interTeamResponse.ok) {
          const data = await interTeamResponse.json();
          interTeamGroups = data.interTeamGroups || [];
        }
        
        const pinnedIds = JSON.parse(localStorage.getItem('pinnedConversations') || '[]');
        
        // Fonction utilitaire : fusionner les données d'équipe dans la liste de conversations
        const mergeTeamData = (convs: Conversation[]): Conversation[] => {
          let result = [...convs];
          
          // Fusionner le groupe d'équipe
          if (teamGroup) {
            const teamIdx = result.findIndex(c => c.id === teamGroup!.id);
            if (teamIdx >= 0) {
              result[teamIdx] = { ...result[teamIdx], ...teamGroup, pinned: true, isTeamGroup: true };
            } else {
              result.unshift({
                ...teamGroup,
                pinned: true,
                isTeamGroup: true,
                unreadCount: 0,
                updatedAt: new Date().toISOString(),
              });
            }
            console.log('[TeamGroup] Groupe d\'équipe chargé:', teamGroup.name);
          }
          
          // Fusionner les groupes inter-équipes
          for (const interGroup of interTeamGroups) {
            const idx = result.findIndex(c => c.id === interGroup.id);
            if (idx >= 0) {
              result[idx] = { ...result[idx], ...interGroup, unreadCount: result[idx].unreadCount || 0 };
            } else {
              result.push({
                ...interGroup,
                unreadCount: interGroup.unreadCount || 0,
                updatedAt: interGroup.updatedAt || new Date().toISOString(),
              });
            }
          }
          if (interTeamGroups.length > 0) {
            console.log('[InterTeam] Groupes inter-équipes chargés/mis à jour:', interTeamGroups.length);
          }
          
          return result;
        };
          
        if (!conversationsLoadedRef.current || showArchivedConversations) {
          // Premier chargement ou vue archives : utiliser les données du serveur
          let convs: Conversation[] = serverConvs.map((c: Conversation) => ({
            ...c,
            pinned: pinnedIds.includes(c.id),
          }));
          
          // Fusionner les données d'équipe AVANT de mettre à jour le state
          convs = mergeTeamData(convs);
          
          setConversations(convs);
          // Initialiser les compteurs locaux
          convs.forEach((c: Conversation) => {
            localUnreadCountsRef.current.set(c.id, c.unreadCount || 0);
          });
          if (!showArchivedConversations) {
            conversationsLoadedRef.current = true;
          }
          console.log('[Conversations] Chargement:', convs.length, 'conversations', showArchivedConversations ? '(archives)' : '');
        } else {
          // Rechargements suivants (mode normal) : mettre à jour les conversations existantes
          // (lastMessage, participants, etc.) tout en préservant les compteurs locaux de non-lus,
          // et ajouter les éventuelles nouvelles conversations
          setConversations(prev => {
            let updated = [...prev];
            let hasChanges = false;
            
            for (const serverConv of serverConvs) {
              const existingIdx = updated.findIndex(c => c.id === serverConv.id);
              if (existingIdx >= 0) {
                // Mettre à jour la conversation existante avec les données fraîches
                // mais préserver le compteur local de non-lus et le flag pinned
                const localUnread = localUnreadCountsRef.current.get(serverConv.id);
                const prevConv = updated[existingIdx];
                updated[existingIdx] = {
                  ...prevConv,
                  ...serverConv,
                  pinned: prevConv.pinned || pinnedIds.includes(serverConv.id),
                  unreadCount: localUnread !== undefined ? localUnread : (serverConv.unreadCount || 0),
                  // Préserver les métadonnées d'équipe si elles existent déjà localement
                  ...(prevConv.isTeamGroup ? { isTeamGroup: true } : {}),
                  ...(prevConv.isInterTeam ? { 
                    isInterTeam: true,
                    otherTeamName: prevConv.otherTeamName,
                    displayName: prevConv.displayName,
                    userTeamId: prevConv.userTeamId,
                    otherTeamId: prevConv.otherTeamId,
                  } : {}),
                };
                hasChanges = true;
              } else {
                // Nouvelle conversation
                updated.push({
                  ...serverConv,
                  pinned: pinnedIds.includes(serverConv.id),
                });
                hasChanges = true;
                console.log('[Conversations] Nouvelle conversation:', serverConv.id);
              }
            }
            
            // Fusionner les données d'équipe (écrase les métadonnées d'équipe avec les données fraîches)
            updated = mergeTeamData(updated);
            
            return hasChanges ? updated : prev;
          });
        }
      } catch (err) {
        console.error('[Conversations] Erreur chargement:', err);
      }
    };

    fetchConversations();
    // Recharger périodiquement pour détecter les nouvelles conversations (seulement en mode normal)
    if (!showArchivedConversations) {
      const interval = setInterval(fetchConversations, 60000);
      return () => clearInterval(interval);
    }
  }, [session.pseudo, session.compet, showArchivedConversations]);

  // Charger les conversations legacy (messages du site webtarot.fr)
  useEffect(() => {
    const fetchLegacyConversations = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/legacy/conversations?pseudo=${encodeURIComponent(session.pseudo)}`
        );
        if (response.ok) {
          const data = await response.json();
          const legacyConvs = data.conversations || [];
          
          // Date limite pour l'archivage automatique (1 an)
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          
          // Filtrer les conversations legacy selon l'état d'archivage (> 1 an)
          const filteredLegacyConvs = legacyConvs.filter((lc: Conversation) => {
            const lastMessageDate = lc.updatedAt ? new Date(lc.updatedAt) : null;
            const isAutoArchived = lastMessageDate && lastMessageDate < oneYearAgo;
            return showArchivedConversations ? isAutoArchived : !isAutoArchived;
          });
          
          // Mettre à jour le compteur d'archives legacy
          if (!showArchivedConversations) {
            const archivedLegacyCount = legacyConvs.filter((lc: Conversation) => {
              const lastMessageDate = lc.updatedAt ? new Date(lc.updatedAt) : null;
              return lastMessageDate && lastMessageDate < oneYearAgo;
            }).length;
            setArchivedCount(prev => prev + archivedLegacyCount);
          }
          
          if (filteredLegacyConvs.length > 0) {
            setConversations(prev => {
              // Filtrer les conversations legacy existantes pour éviter les doublons
              const existingLegacyIds = new Set(prev.filter(c => c.isLegacy).map(c => c.id));
              const newLegacyConvs = filteredLegacyConvs.filter((lc: Conversation) => !existingLegacyIds.has(lc.id));
              
              if (newLegacyConvs.length > 0) {
                console.log('[Legacy] Conversations chargées:', newLegacyConvs.length, showArchivedConversations ? '(archives)' : '');
                return [...prev, ...newLegacyConvs];
              }
              return prev;
            });
          }
        }
      } catch (err) {
        console.error('[Legacy] Erreur chargement:', err);
      }
    };

    // Charger les conversations legacy après un petit délai (priorité aux conversations Neo)
    const timeout = setTimeout(fetchLegacyConversations, 1000);
    return () => clearTimeout(timeout);
  }, [session.pseudo, showArchivedConversations]);

  // Note : le chargement du groupe d'équipe et des groupes inter-équipes
  // est désormais intégré dans l'effet principal de chargement des conversations
  // (ci-dessus) via Promise.all pour éviter un flash visuel lors de la mise
  // à jour des métadonnées d'équipe (avatar, nom d'affichage, etc.)
  
  // Recalculer le total des non-lus quand les conversations changent
  useEffect(() => {
    const total = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    setTotalUnreadCount(total);
  }, [conversations]);

  // Charger les messages d'une conversation
  const loadConversationMessages = useCallback(async (conversationId: string) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${conversationId}/messages?limit=50`
      );
      if (response.ok) {
        const data = await response.json();
        setDirectMessages(data.messages || []);
        // Scroll en bas après chargement
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 100);
      }
    } catch (err) {
      console.error('[Conversations] Erreur messages:', err);
    }
  }, []);

  // Supprimer les conversations vides (one-to-one sans messages)
  const removeEmptyConversations = useCallback((exceptId?: string) => {
    setConversations(prev => prev.filter(c => {
      // Ne pas supprimer la conversation qu'on va ouvrir
      if (c.id === exceptId) return true;
      // Ne pas supprimer les groupes
      if (c.type === 'group') return true;
      // Garder les conversations avec des messages
      if (c.lastMessage) return true;
      // Supprimer les conversations directes vides
      console.log('[Conversations] Suppression conversation vide:', c.id);
      return false;
    }));
  }, []);

  // Charger les messages d'une conversation legacy
  const loadLegacyMessages = useCallback(async (correspondant: string) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/legacy/conversations/${encodeURIComponent(correspondant)}/messages?pseudo=${encodeURIComponent(session.pseudo)}`
      );
      if (response.ok) {
        const data = await response.json();
        const messages = (data.messages || []).map((msg: {
          id: string;
          text: string;
          author: string;
          authorPseudo: string;
          createdAt: string;
          status: string;
          titre?: string;
          isLegacy: boolean;
        }) => ({
          id: msg.id,
          text: msg.text,
          author: msg.author,
          createdAt: msg.createdAt,
          status: msg.status,
          isLegacy: true,
          titre: msg.titre,
        }));
        setDirectMessages(messages);
        console.log('[Legacy] Messages chargés:', messages.length);
      }
    } catch (err) {
      console.error('[Legacy] Erreur chargement messages:', err);
      setDirectMessages([]);
    }
  }, [session.pseudo]);

  // Sélectionner une conversation
  const selectConversation = useCallback(async (conversation: Conversation) => {
    console.log('[Conversations] Sélection:', conversation.id, 'unread:', conversation.unreadCount, 'legacy:', conversation.isLegacy);
    
    // Quitter la conversation précédente si nécessaire
    if (activeConversation && activeConversation.id !== conversation.id && !activeConversation.isLegacy) {
      socket?.emit('leave_conversation', activeConversation.id);
    }
    
    // Supprimer les conversations vides (sauf celle qu'on ouvre et sauf legacy)
    if (!conversation.isLegacy) {
      removeEmptyConversations(conversation.id);
    }
    
    // Mettre à jour le compteur local immédiatement (UX réactive)
    setConversations(prev => prev.map(c => 
      c.id === conversation.id ? { ...c, unreadCount: 0 } : c
    ));
    
    setActiveConversation({ ...conversation, unreadCount: 0 });
    setActiveView('direct');
    setShowMobileDetail(true);
    setTypingUsers([]); // Réinitialiser les indicateurs de frappe
    
    // Charger les messages selon le type de conversation
    if (conversation.isLegacy && conversation.legacyCorrespondant) {
      await loadLegacyMessages(conversation.legacyCorrespondant);
    } else {
      await loadConversationMessages(conversation.id);
      
      // Marquer comme lu côté serveur (seulement pour les conversations Neo)
      try {
        await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo: session.pseudo }),
        });
      } catch (err) {
        console.error('[Conversations] Erreur marquage lu:', err);
      }

      // Rejoindre la room Socket.IO (seulement pour les conversations Neo)
      socket?.emit('join_conversation', conversation.id);
    }
    
    // Focus sur l'input de message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [loadConversationMessages, loadLegacyMessages, session.pseudo, socket, activeConversation, removeEmptyConversations]);

  // Revenir au chat global
  const selectGlobalChat = useCallback(() => {
    if (activeConversation) {
      socket?.emit('leave_conversation', activeConversation.id);
    }

    removeEmptyConversations();

    setActiveConversation(null);
    setDirectMessages([]);

    if (socket) {
      socket.emit('leave_global');
      socket.emit('join_global', {
        pseudo: session.pseudo,
        compet: session.compet,
      });
    }

    setActiveView('global');
    setShowMobileDetail(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [activeConversation, socket, removeEmptyConversations, session.pseudo, session.compet]);

  const selectBetaChat = useCallback(() => {
    if (activeConversation) {
      socket?.emit('leave_conversation', activeConversation.id);
    }

    removeEmptyConversations();

    setActiveConversation(null);
    setDirectMessages([]);

    if (socket) {
      socket.emit('leave_global');
      socket.emit('join_global_beta', { pseudo: session.pseudo });
    }

    setActiveView('beta');
    setShowMobileDetail(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [activeConversation, socket, removeEmptyConversations, session.pseudo]);

  // Créer une conversation directe (ou ouvrir l'existante)
  const createDirectConversation = useCallback(async (targetPseudo: string) => {
    // Vérifier si une conversation existe déjà avec ce joueur
    const existingConv = conversations.find(c => 
      c.type === 'direct' && 
      c.participants.some(p => p.toLowerCase() === targetPseudo.toLowerCase())
    );
    
    if (existingConv) {
      // Ouvrir la conversation existante
      console.log('[Conversations] Conversation existante trouvée:', existingConv.id);
      selectConversation(existingConv);
      setShowNewConversationModal(false);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pseudo: session.pseudo,
          targetPseudo,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Recharger les conversations
        const convResponse = await fetch(
          `${BACKEND_URL}/api/conversations?pseudo=${encodeURIComponent(session.pseudo)}`
        );
        if (convResponse.ok) {
          const convData = await convResponse.json();
          setConversations(convData.conversations || []);
          // Sélectionner la nouvelle conversation
          const newConv = convData.conversations.find(
            (c: Conversation) => c.id === data.conversation.id
          );
          if (newConv) {
            selectConversation(newConv);
          }
        }
        setShowNewConversationModal(false);
      }
    } catch (err) {
      console.error('[Conversations] Erreur création:', err);
    }
  }, [session.pseudo, selectConversation, conversations]);

  // Créer un groupe
  const createGroupConversation = useCallback(async (
    participants: string[],
    name: string,
    avatarEmoji: string,
    avatarColor: string
  ) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pseudo: session.pseudo,
          participants,
          name,
          avatarEmoji,
          avatarColor,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Recharger les conversations
        const convResponse = await fetch(
          `${BACKEND_URL}/api/conversations?pseudo=${encodeURIComponent(session.pseudo)}`
        );
        if (convResponse.ok) {
          const convData = await convResponse.json();
          setConversations(convData.conversations || []);
          // Sélectionner le nouveau groupe
          const newConv = convData.conversations.find(
            (c: Conversation) => c.id === data.conversation.id
          );
          if (newConv) {
            selectConversation(newConv);
          }
        }
        setShowNewConversationModal(false);
      }
    } catch (err) {
      console.error('[Conversations] Erreur création groupe:', err);
    }
  }, [session.pseudo, selectConversation]);

  // Créer un groupe inter-équipes
  const createInterTeamGroup = useCallback(async (otherTeamId: number) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/inter-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pseudo: session.pseudo,
          otherTeamId,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const newConv: Conversation = {
          ...data.conversation,
          unreadCount: 0,
          updatedAt: new Date().toISOString(),
        };
        
        setConversations(prev => {
          // Vérifier si le groupe existe déjà
          const exists = prev.some(c => c.id === newConv.id);
          if (exists) {
            return prev;
          }
          return [...prev, newConv];
        });
        
        selectConversation(newConv);
        setShowNewConversationModal(false);
        console.log('[InterTeam] Groupe créé:', data.conversation.name);
      }
    } catch (err) {
      console.error('[InterTeam] Erreur création:', err);
    }
  }, [session.pseudo, selectConversation]);

  // Épingler/désépingler une conversation (stocké localement)
  const togglePinConversation = useCallback((conversationId: string) => {
    setConversations(prev => {
      const updated = prev.map(c => 
        c.id === conversationId ? { ...c, pinned: !c.pinned } : c
      );
      // Sauvegarder en localStorage
      const pinnedIds = updated.filter(c => c.pinned).map(c => c.id);
      localStorage.setItem('pinnedConversations', JSON.stringify(pinnedIds));
      return updated;
    });
  }, []);

  // Mettre à jour les paramètres d'un groupe
  const updateGroupSettings = useCallback(async (settings: { name?: string; avatarEmoji?: string; avatarColor?: string }) => {
    if (!activeConversation) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/${activeConversation.id}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Mettre à jour localement
        setConversations(prev => prev.map(c => 
          c.id === activeConversation.id 
            ? { ...c, ...settings }
            : c
        ));
        setActiveConversation(prev => prev ? { ...prev, ...settings } : null);
        console.log('[GroupSettings] Paramètres mis à jour:', data);
      }
    } catch (err) {
      console.error('[GroupSettings] Erreur mise à jour:', err);
    }
  }, [activeConversation]);

  // Ajouter un membre au groupe
  const addGroupMember = useCallback(async (pseudo: string) => {
    if (!activeConversation) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/${activeConversation.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo }),
      });
      
      if (response.ok) {
        // Mettre à jour localement
        setConversations(prev => prev.map(c => 
          c.id === activeConversation.id 
            ? { ...c, participants: [...c.participants, pseudo.toLowerCase()] }
            : c
        ));
        setActiveConversation(prev => 
          prev ? { ...prev, participants: [...prev.participants, pseudo.toLowerCase()] } : null
        );
        console.log('[GroupSettings] Membre ajouté:', pseudo);
      }
    } catch (err) {
      console.error('[GroupSettings] Erreur ajout membre:', err);
    }
  }, [activeConversation]);

  // Retirer un membre du groupe
  const removeGroupMember = useCallback(async (pseudo: string) => {
    if (!activeConversation) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/members/${encodeURIComponent(pseudo)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        // Mettre à jour localement
        setConversations(prev => prev.map(c => 
          c.id === activeConversation.id 
            ? { ...c, participants: c.participants.filter(p => p.toLowerCase() !== pseudo.toLowerCase()) }
            : c
        ));
        setActiveConversation(prev => 
          prev ? { ...prev, participants: prev.participants.filter(p => p.toLowerCase() !== pseudo.toLowerCase()) } : null
        );
        console.log('[GroupSettings] Membre retiré:', pseudo);
      }
    } catch (err) {
      console.error('[GroupSettings] Erreur retrait membre:', err);
    }
  }, [activeConversation]);

  // Supprimer un groupe
  const deleteGroup = useCallback(async () => {
    if (!activeConversation) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/${activeConversation.id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        // Retirer de la liste et revenir au chat global
        setConversations(prev => prev.filter(c => c.id !== activeConversation.id));
        setShowGroupSettings(false);
        selectGlobalChat();
        console.log('[GroupSettings] Groupe supprimé');
      }
    } catch (err) {
      console.error('[GroupSettings] Erreur suppression:', err);
    }
  }, [activeConversation, selectGlobalChat]);

  // Insérer un emoji dans le champ de texte
  const insertEmoji = useCallback((emoji: string) => {
    const input = inputRef.current;
    if (!input) {
      setInputText(prev => prev + emoji);
      return;
    }
    
    // Insérer à la position du curseur
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newText = inputText.slice(0, start) + emoji + inputText.slice(end);
    setInputText(newText);
    
    // Repositionner le curseur après l'emoji
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  }, [inputText]);

  const canUseAttachMenu = isGlobalSalonView || (activeView === 'direct' && !!activeConversation);

  const directOtherPseudo = useMemo(() => {
    if (!isOneToOneConversation(activeConversation)) return null;
    if (activeConversation!.isLegacy && activeConversation!.legacyCorrespondant) {
      return activeConversation!.legacyCorrespondant;
    }
    const other = activeConversation!.participants.find(
      (p) => p.toLowerCase() !== session.pseudo.toLowerCase(),
    );
    return other ?? null;
  }, [activeConversation, session.pseudo]);

  const ciaOpponentMatches = useCallback((
    opponent: { pseudo: string; pseudo_display: string },
    otherPseudo: string,
  ) => {
    const needle = otherPseudo.toLowerCase();
    return opponent.pseudo.toLowerCase() === needle
      || opponent.pseudo_display.toLowerCase() === needle;
  }, []);

  useEffect(() => {
    if (activeView !== 'direct' || !directOtherPseudo) {
      setCiaRdvPhaseId(null);
      setCiaRdvOpponent(null);
      return;
    }

    let cancelled = false;
    setCiaRdvChecking(true);
    (async () => {
      try {
        // Même logique que la page CIA (participants), pas /challenges/cia/opponents
        // qui exclut les phases encours hors fenêtre debut/fin calendaire.
        const meRes = await fetch(`${BACKEND_URL}/api/competition/cia/me`, {
          headers: { 'X-User-Pseudo': session.pseudo },
        });
        const meJson = await meRes.json();
        if (cancelled) return;
        const phaseId = meJson.data?.phase?.id != null ? Number(meJson.data.phase.id) : null;
        const divisionId = meJson.data?.participant?.division_id != null
          ? Number(meJson.data.participant.division_id)
          : null;
        if (!meJson.success || !phaseId || !divisionId) {
          setCiaRdvPhaseId(null);
          setCiaRdvOpponent(null);
          return;
        }

        const partRes = await fetch(
          `${BACKEND_URL}/api/competition/cia/${phaseId}/divisions/${divisionId}/participants`,
          { headers: { 'X-User-Pseudo': session.pseudo } },
        );
        const partJson = await partRes.json();
        if (cancelled) return;
        if (!partJson.success) {
          setCiaRdvPhaseId(null);
          setCiaRdvOpponent(null);
          return;
        }

        const opponents = (partJson.data?.items ?? []) as Array<{
          pseudo: string;
          pseudo_display: string;
          can_challenge: boolean;
        }>;
        const match = opponents.find(
          (o) => o.can_challenge && ciaOpponentMatches(o, directOtherPseudo),
        );
        setCiaRdvPhaseId(match ? phaseId : null);
        setCiaRdvOpponent(match ? { pseudo: match.pseudo, display: match.pseudo_display } : null);
      } catch {
        if (!cancelled) {
          setCiaRdvPhaseId(null);
          setCiaRdvOpponent(null);
        }
      } finally {
        if (!cancelled) setCiaRdvChecking(false);
      }
    })();

    return () => { cancelled = true; };
  }, [session.pseudo, activeView, directOtherPseudo, ciaOpponentMatches]);

  const ciaRdvAttachMeta = useMemo(() => {
    if (
      ciaRdvChecking
      || !isOneToOneConversation(activeConversation)
      || !ciaRdvOpponent
      || !ciaRdvPhaseId
    ) {
      return null;
    }
    return {
      title: 'Rendez-vous défi CIA',
      subtitle: `Programmer votre défi contre ${ciaRdvOpponent.display}`,
    };
  }, [activeConversation, ciaRdvChecking, ciaRdvOpponent, ciaRdvPhaseId]);

  const isScrolledNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= 48;
  }, []);

  const scrollToBottomIfNeeded = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (!isScrolledNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }
  }, [isScrolledNearBottom]);

  const gameInviteMeta = useMemo(() => {
    const hasActiveDraft = !!sessionStorage.getItem('wt_active_draft');
    const isDirect = activeConversation?.type === 'direct';
    const isGlobal = activeView === 'global';
    const otherName = activeConversation
      ? formatOtherParticipantNames(activeConversation.participants, session.pseudo)
      : '';
    const inviteTarget = isDirect && otherName
      ? otherName
      : isGlobal
        ? 'le salon'
        : 'le groupe';
    const proposeTarget = isDirect && otherName
      ? `à ${otherName}`
      : isGlobal
        ? 'au salon'
        : 'au groupe';

    if (gameInfo?.neo && gameInfo.neoTableId) {
      return {
        title: 'Inviter à ma table',
        subtitle: `Invitez ${inviteTarget} à rejoindre une nouvelle table`,
      };
    }

    if (hasActiveDraft) {
      return {
        title: 'Inviter à ma table',
        subtitle: `Invitez ${inviteTarget} à rejoindre une nouvelle table`,
      };
    }

    if (gameInfo) {
      return {
        title: 'Inviter à jouer',
        subtitle: 'Vous êtes en partie — une nouvelle table sera proposée',
      };
    }

    return {
      title: 'Inviter à jouer',
      subtitle: `Proposez ${proposeTarget} de créer une nouvelle table`,
    };
  }, [gameInfo, activeConversation, activeView, session.pseudo]);

  const handleAttachPoll = useCallback(() => {
    showToast({
      message: 'Les sondages dans les discussions arrivent bientôt',
      type: 'info',
      duration: 4000,
    });
  }, [showToast]);

  const handleAttachCiaRdv = useCallback(() => {
    if (!ciaRdvAttachMeta || !ciaRdvOpponent) return;
    setShowCiaRdvModal(true);
  }, [ciaRdvAttachMeta, ciaRdvOpponent]);

  const handleSubmitCiaRdvFromChat = useCallback(async (rdvIso: string) => {
    if (!ciaRdvPhaseId || !ciaRdvOpponent) return;
    const res = await fetch(`${BACKEND_URL}/api/competition/cia/${ciaRdvPhaseId}/agenda`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Pseudo': session.pseudo,
      },
      body: JSON.stringify({ adversaire: ciaRdvOpponent.pseudo, rdv: rdvIso }),
    });
    const json = await res.json().catch(() => ({ success: false, error: res.statusText }));
    if (!res.ok || !json.success) {
      throw new Error(
        typeof json.error === 'string'
          ? json.error
          : 'Impossible d\'envoyer la proposition de RDV',
      );
    }
    showToast({
      message: `Proposition de RDV CIA envoyée à ${ciaRdvOpponent.display}`,
      type: 'info',
      duration: 4500,
    });
  }, [ciaRdvPhaseId, ciaRdvOpponent, session.pseudo, showToast]);

  const offlineInviteParticipantNames = useMemo(() => {
    if (!activeConversation) return '';
    return formatOtherParticipantNames(activeConversation.participants, session.pseudo);
  }, [activeConversation, session.pseudo]);

  const pendingGlobalGameInviteMessage = useMemo(() => {
    if (activeView !== 'global') return null;
    return messages
      .filter((m) =>
        m.messageType === 'game_invite'
        && m.gameInvite?.status === 'pending'
        && m.pseudo.toLowerCase() === session.pseudo.toLowerCase(),
      )
      .sort((a, b) => b.timestamp - a.timestamp)[0] ?? null;
  }, [messages, activeView, session.pseudo]);

  const pendingGameInviteMessage = useMemo(() => {
    if (!activeConversation) return null;
    return directMessages
      .filter((m) =>
        m.conversationId === activeConversation.id
        && m.messageType === 'game_invite'
        && m.gameInvite?.status === 'pending'
        && m.author.toLowerCase() === session.pseudo.toLowerCase(),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
  }, [directMessages, activeConversation, session.pseudo]);

  const sendGameInvite = useCallback(async () => {
    if (!activeConversation) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/game-invites`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pseudo: session.pseudo,
            compet: session.compet,
          }),
        },
      );

      if (!response.ok) {
        showToast({
          message: 'Impossible d\'envoyer l\'invitation',
          type: 'info',
          duration: 4500,
        });
        return;
      }

      const data = await response.json();
      const now = new Date().toISOString();
      setDirectMessages((prev) => {
        if (prev.some((m) => m.id === data.messageId)) return prev;
        return [...prev, {
          id: data.messageId,
          conversationId: activeConversation.id,
          author: session.pseudo,
          text: data.gameInvite ? `🎮 Invitation à jouer` : 'Invitation à jouer',
          createdAt: now,
          status: 'sent',
          messageType: 'game_invite',
          gameInvite: data.gameInvite,
        }];
      });

      showToast({
        message: 'Invitation publiée dans la conversation',
        type: 'game',
        duration: 4000,
      });

      if (data.gameInvite?.draftId) {
        sessionStorage.setItem('wt_active_draft', data.gameInvite.draftId);
      }

      setTimeout(() => {
        scrollToBottomIfNeeded('smooth');
      }, 50);
    } catch (err) {
      console.error('[Chat] Erreur invitation jeu:', err);
      showToast({
        message: 'Impossible d\'envoyer l\'invitation',
        type: 'info',
        duration: 4000,
      });
    }
  }, [activeConversation, session.pseudo, session.compet, showToast, scrollToBottomIfNeeded]);

  const sendGlobalGameInvite = useCallback(async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/global/game-invites`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pseudo: session.pseudo,
            compet: session.compet,
          }),
        },
      );

      if (!response.ok) {
        showToast({
          message: 'Impossible d\'envoyer l\'invitation',
          type: 'info',
          duration: 4500,
        });
        return;
      }

      const data = await response.json();
      const timestamp = Date.now();
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === data.messageId)) return prev;
        return [...prev, {
          roomId: standardGlobalRoomId,
          pseudo: session.pseudo,
          text: data.gameInvite ? `🎮 Invitation à jouer` : 'Invitation à jouer',
          timestamp,
          source: 'app',
          messageId: data.messageId,
          messageType: 'game_invite',
          gameInvite: data.gameInvite,
        }];
      });

      showToast({
        message: 'Invitation publiée dans le salon',
        type: 'game',
        duration: 4000,
      });

      if (data.gameInvite?.draftId) {
        sessionStorage.setItem('wt_active_draft', data.gameInvite.draftId);
      }

      setTimeout(() => {
        scrollToBottomIfNeeded('smooth');
      }, 50);
    } catch (err) {
      console.error('[Chat] Erreur invitation salon:', err);
      showToast({
        message: 'Impossible d\'envoyer l\'invitation',
        type: 'info',
        duration: 4000,
      });
    }
  }, [standardGlobalRoomId, session.pseudo, session.compet, showToast, scrollToBottomIfNeeded]);

  const requestGameInviteAfterChecks = useCallback(() => {
    if (!activeConversation) return;

    const connected = getConnectedTeammates(
      activeConversation.participants,
      session.pseudo,
      connectedUsers,
    );

    if (connected.length === 0) {
      setShowOfflineInviteConfirm(true);
      return;
    }

    void sendGameInvite();
  }, [activeConversation, session.pseudo, connectedUsers, sendGameInvite]);

  const handleAttachGameInvite = useCallback(() => {
    if (activeView === 'global') {
      if (pendingGlobalGameInviteMessage?.messageId) {
        setShowExistingInviteConfirm(true);
        return;
      }
      void sendGlobalGameInvite();
      return;
    }

    if (activeView === 'beta') {
      showToast({
        message: 'Les invitations à jouer ne sont pas disponibles dans le salon bêta',
        type: 'info',
        duration: 4000,
      });
      return;
    }

    if (!activeConversation || activeView !== 'direct') {
      showToast({
        message: 'Disponible dans une conversation privée, de groupe ou le salon global',
        type: 'info',
        duration: 4000,
      });
      return;
    }

    if (pendingGameInviteMessage) {
      setShowExistingInviteConfirm(true);
      return;
    }

    requestGameInviteAfterChecks();
  }, [
    activeConversation,
    activeView,
    pendingGameInviteMessage,
    pendingGlobalGameInviteMessage,
    requestGameInviteAfterChecks,
    sendGlobalGameInvite,
    showToast,
  ]);

  const salonGameInviteMeta = useMemo(() => {
    const hasActiveDraft = !!sessionStorage.getItem('wt_active_draft');

    if (gameInfo?.neo && gameInfo.neoTableId) {
      return {
        title: 'Inviter à ma table',
        subtitle: 'Invitez le salon à rejoindre une nouvelle table',
      };
    }

    if (hasActiveDraft) {
      return {
        title: 'Inviter à ma table',
        subtitle: 'Invitez le salon à rejoindre une nouvelle table',
      };
    }

    if (gameInfo) {
      return {
        title: 'Inviter à jouer',
        subtitle: 'Vous êtes en partie — une nouvelle table sera proposée',
      };
    }

    return {
      title: 'Inviter à jouer',
      subtitle: 'Proposez au salon de créer une nouvelle table',
    };
  }, [gameInfo]);

  const handleGlobalGameInvite = useCallback(() => {
    if (pendingGlobalGameInviteMessage?.messageId) {
      setShowExistingInviteConfirm(true);
      return;
    }
    void sendGlobalGameInvite();
  }, [pendingGlobalGameInviteMessage, sendGlobalGameInvite]);

  const navigateToDraft = useCallback((draftId: string, gameMode?: string) => {
    sessionStorage.setItem('wt_active_draft', draftId);
    setActiveTab('tables');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('join-draft-from-invitation', {
        detail: { draftId, gameMode },
      }));
    }, 100);
  }, [setActiveTab]);

  const respondToGlobalGameInvite = useCallback(async (messageId: string, accepted: boolean) => {
    const response = await fetch(
      `${BACKEND_URL}/api/global/messages/${messageId}/game-invite/respond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo, accepted, compet: session.compet }),
      },
    );
    if (!response.ok) {
      showToast({ message: 'Impossible de répondre à l\'invitation', type: 'info' });
      return;
    }
    const data = await response.json();
    setMessages((prev) => prev.map((m) =>
      (m.messageId || `${m.timestamp}-${m.pseudo}`) === messageId
        ? { ...m, gameInvite: data.gameInvite }
        : m,
    ));

    if (accepted && data.gameInvite?.draftId) {
      navigateToDraft(data.gameInvite.draftId, data.gameInvite.config?.gameMode);
    }
  }, [session.pseudo, session.compet, showToast, navigateToDraft]);

  const respondToGameInvite = useCallback(async (messageId: string, accepted: boolean) => {
    if (!activeConversation) return;
    const response = await fetch(
      `${BACKEND_URL}/api/conversations/${activeConversation.id}/messages/${messageId}/game-invite/respond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo, accepted }),
      },
    );
    if (!response.ok) {
      showToast({ message: 'Impossible de répondre à l\'invitation', type: 'info' });
      return;
    }
    const data = await response.json();
    setDirectMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, gameInvite: data.gameInvite, text: m.text } : m,
    ));

    if (accepted && data.gameInvite?.draftId) {
      navigateToDraft(data.gameInvite.draftId, data.gameInvite.config?.gameMode);
    }
  }, [activeConversation, session.pseudo, showToast, navigateToDraft]);

  const cancelGlobalGameInvite = useCallback(async (messageId: string): Promise<boolean> => {
    const response = await fetch(
      `${BACKEND_URL}/api/global/messages/${messageId}/game-invite/cancel`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo, compet: session.compet }),
      },
    );
    if (!response.ok) {
      showToast({ message: 'Impossible d\'annuler l\'invitation', type: 'info' });
      return false;
    }
    const data = await response.json();
    setMessages((prev) => prev.map((m) =>
      (m.messageId || `${m.timestamp}-${m.pseudo}`) === messageId
        ? { ...m, gameInvite: data.gameInvite }
        : m,
    ));
    return true;
  }, [session.pseudo, session.compet, showToast]);

  const cancelGameInvite = useCallback(async (messageId: string): Promise<boolean> => {
    if (!activeConversation) return false;
    const response = await fetch(
      `${BACKEND_URL}/api/conversations/${activeConversation.id}/messages/${messageId}/game-invite/cancel`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo }),
      },
    );
    if (!response.ok) {
      showToast({ message: 'Impossible d\'annuler l\'invitation', type: 'info' });
      return false;
    }
    const data = await response.json();
    setDirectMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, gameInvite: data.gameInvite } : m,
    ));
    return true;
  }, [activeConversation, session.pseudo, showToast]);

  const respondToCiaAgendaInvite = useCallback(async (invite: CiaAgendaInvitePayload, accepted: boolean) => {
    const action = accepted ? 'accept' : 'refuse';
    try {
      const res = await fetch(`${BACKEND_URL}/api/competition/cia/agenda/${invite.agendaId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Pseudo': session.pseudo,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        showToast({ message: json.error || 'Impossible de répondre', type: 'info' });
        return;
      }
      showToast({
        message: accepted ? 'Rendez-vous CIA accepté' : 'Proposition CIA refusée',
        type: 'info',
      });
    } catch {
      showToast({ message: 'Impossible de répondre à la proposition CIA', type: 'info' });
    }
  }, [session.pseudo, showToast]);

  const cancelCiaAgendaInvite = useCallback(async (invite: CiaAgendaInvitePayload) => {
    const isProposer = invite.proposerPseudo.toLowerCase() === session.pseudo.toLowerCase();
    const action = isProposer ? 'cancel' : 'cancel-destinataire';
    try {
      const res = await fetch(`${BACKEND_URL}/api/competition/cia/agenda/${invite.agendaId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Pseudo': session.pseudo,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        showToast({ message: json.error || 'Impossible d\'annuler', type: 'info' });
        return false;
      }
      return true;
    } catch {
      showToast({ message: 'Impossible d\'annuler le rendez-vous CIA', type: 'info' });
      return false;
    }
  }, [session.pseudo, showToast]);

  const reportGlobalMessage = useCallback(async (message: ChatMessage) => {
    if (!session.token) {
      showToast({ message: 'Connexion requise pour signaler', type: 'info' });
      return;
    }
    if (!window.confirm(`Signaler le message de ${message.pseudo} ?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/moderation/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({
          target: message.pseudo,
          roomId: message.roomId || standardGlobalRoomId,
          messageId: message.messageId,
          textSnapshot: message.text?.slice(0, 500),
        }),
      });
      if (!res.ok) {
        showToast({ message: 'Impossible d\'envoyer le signalement', type: 'info' });
        return;
      }
      showToast({ message: 'Signalement envoyé — merci', type: 'info' });
    } catch {
      showToast({ message: 'Impossible d\'envoyer le signalement', type: 'info' });
    }
  }, [session.token, standardGlobalRoomId, showToast]);

  const cancelLibraryShare = useCallback(async (sharedGameId: number): Promise<boolean> => {
    if (!window.confirm('Annuler ce partage ? Les destinataires n\'y auront plus accès.')) {
      return false;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}?pseudo=${encodeURIComponent(session.pseudo)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        showToast({ message: 'Impossible d\'annuler le partage', type: 'info' });
        return false;
      }
      return true;
    } catch {
      showToast({ message: 'Impossible d\'annuler le partage', type: 'info' });
      return false;
    }
  }, [session.pseudo, showToast]);

  const handleReplaceExistingGameInvite = useCallback(async () => {
    setShowExistingInviteConfirm(false);

    if (activeView === 'global' && pendingGlobalGameInviteMessage?.messageId) {
      const cancelled = await cancelGlobalGameInvite(pendingGlobalGameInviteMessage.messageId);
      if (cancelled) {
        void sendGlobalGameInvite();
      }
      return;
    }

    if (!pendingGameInviteMessage) return;

    const cancelled = await cancelGameInvite(pendingGameInviteMessage.id);
    if (cancelled) {
      requestGameInviteAfterChecks();
    }
  }, [
    activeView,
    pendingGameInviteMessage,
    pendingGlobalGameInviteMessage,
    cancelGameInvite,
    cancelGlobalGameInvite,
    requestGameInviteAfterChecks,
    sendGlobalGameInvite,
  ]);

  const handleInputChange = useCallback((value: string) => {
    setInputText(value);

    if (activeView === 'direct' && activeConversation && socket && value.length > 0) {
      const now = Date.now();
      if (now - lastTypingEmitRef.current > 1000) {
        socket.emit('typing_start', { conversationId: activeConversation.id });
        lastTypingEmitRef.current = now;
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        if (socket && activeConversation) {
          socket.emit('typing_stop', { conversationId: activeConversation.id });
        }
      }, 2000);
    }
  }, [activeView, activeConversation, socket]);

  // Envoyer un message privé
  const sendDirectMessage = useCallback(async () => {
    if (!activeConversation || !inputText.trim()) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pseudo: session.pseudo,
            text: inputText.trim(),
            ...(replyingTo ? { replyToId: replyingTo.id } : {}),
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const now = new Date().toISOString();
        
        // Ajouter le message localement avec un ID temporaire pour éviter les doublons
        const tempId = `temp-${Date.now()}`;
        setDirectMessages(prev => {
          // Vérifier si le message existe déjà
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, { ...data.message, tempId }];
        });
        
        // Mettre à jour la conversation dans la liste (lastMessage + réorganisation)
        setConversations(prev => prev.map(c => {
          if (c.id === activeConversation.id) {
            return {
              ...c,
              lastMessage: {
                text: data.message.text,
                author: session.pseudo,
                timestamp: now,
                status: data.message.status || 'sent',
              },
              updatedAt: now,
            };
          }
          return c;
        }));
        
        setInputText('');
        setReplyingTo(null);
        inputRef.current?.focus();
        
        // Scroll en bas après envoi
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
        
        // NOTE: On n'émet PAS via Socket.IO ici car l'API HTTP s'en charge
        // Cela évite les doublons de messages
      }
    } catch (err) {
      console.error('[Conversations] Erreur envoi:', err);
    }
  }, [activeConversation, inputText, session.pseudo, replyingTo]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (socket && activeConversation) {
        socket.emit('typing_stop', { conversationId: activeConversation.id });
      }
      if (isGlobalSalonView) {
        sendMessage();
      } else {
        sendDirectMessage();
      }
    }
  }, [isGlobalSalonView, activeConversation, socket, sendMessage, sendDirectMessage]);

  const handleInputSend = useCallback(() => {
    if (isGlobalSalonView) {
      sendMessage();
    } else {
      sendDirectMessage();
    }
  }, [isGlobalSalonView, sendMessage, sendDirectMessage]);

  const handleGlobalChatSend = useCallback(() => {
    sendMessage();
  }, [sendMessage]);

  const handleGlobalChatKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const scrollToQuotedMessage = useCallback((messageId: string) => {
    const element = document.querySelector(`[data-message-id="${messageId}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleStartReply = useCallback((target: ReplyTarget) => {
    setReplyingTo(target);
    inputRef.current?.focus();
  }, []);

  const toggleGlobalReaction = useCallback((messageId: string, emoji: string) => {
    if (!socket) return;
    socket.emit('chat_reaction', { messageId, emoji });
  }, [socket]);

  useEffect(() => {
    setGlobalChatPanel({
      reactions: globalReactions,
      onToggleReaction: toggleGlobalReaction,
      onReply: handleStartReply,
      onRespondToGameInvite: respondToGlobalGameInvite,
      onCancelGameInvite: cancelGlobalGameInvite,
      onAccessDraft: navigateToDraft,
      onScrollToQuote: scrollToQuotedMessage,
      inputText,
      onInputChange: handleInputChange,
      onSend: handleGlobalChatSend,
      onKeyDown: handleGlobalChatKeyDown,
      replyingTo,
      onDismissReply: () => setReplyingTo(null),
      insertEmoji,
      inputRef,
      connected,
      onPoll: handleAttachPoll,
      onGameInvite: handleGlobalGameInvite,
      gameInviteTitle: salonGameInviteMeta.title,
      gameInviteSubtitle: salonGameInviteMeta.subtitle,
      gameInviteDisabled: !connected,
    });

    return () => setGlobalChatPanel(null);
  }, [
    setGlobalChatPanel,
    globalReactions,
    toggleGlobalReaction,
    handleStartReply,
    respondToGlobalGameInvite,
    cancelGlobalGameInvite,
    navigateToDraft,
    scrollToQuotedMessage,
    inputText,
    handleInputChange,
    handleGlobalChatSend,
    handleGlobalChatKeyDown,
    replyingTo,
    insertEmoji,
    connected,
    handleAttachPoll,
    handleGlobalGameInvite,
    salonGameInviteMeta,
  ]);

  const toggleDirectReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!activeConversation) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/messages/${messageId}/reactions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo: session.pseudo, emoji }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDirectMessages((prev) => prev.map((m) =>
          m.id === messageId ? { ...m, reactions: data.reactions } : m
        ));
      }
    } catch (err) {
      console.error('[Conversations] Erreur réaction:', err);
    }
  }, [activeConversation, session.pseudo]);

  useEffect(() => {
    setReplyingTo(null);
  }, [activeView, activeConversation?.id]);

  // Écouter les messages privés entrants
  useEffect(() => {
    if (!socket) return;

    const handleDirectMessage = (message: DirectMessage & { conversationId: string }) => {
      console.log('[Socket] Message privé reçu:', message.conversationId, 'de', message.author);
      
      // Ignorer si c'est notre propre message (déjà ajouté localement)
      if (message.author.toLowerCase() === session.pseudo.toLowerCase()) {
        console.log('[Socket] Message ignoré (notre propre message)');
        return;
      }

      if (message.messageType === 'library_share' && message.libraryShare) {
        dispatchLibraryNewShare({
          sharedGameId: message.libraryShare.sharedGameId,
          isPoll: message.libraryShare.isPoll,
        });
      }
      
      const isActiveConversation = activeConversation && message.conversationId === activeConversation.id;
      console.log('[Socket] Est conversation active:', isActiveConversation);
      
      if (isActiveConversation) {
        setDirectMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          // S'assurer que createdAt est défini
          const messageWithDate = {
            ...message,
            createdAt: message.createdAt || new Date().toISOString(),
          };
          return [...prev, messageWithDate];
        });
        // Scroll en bas pour les nouveaux messages
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
        
        // Marquer automatiquement comme lu puisque la conversation est active
        // (le message vient d'apparaître, donc l'utilisateur l'a vu)
        fetch(`${BACKEND_URL}/api/conversations/${message.conversationId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo: session.pseudo }),
        }).catch(err => console.error('[Socket] Erreur marquage lu automatique:', err));
      }
      
      // Mettre à jour la conversation (lastMessage, unreadCount, updatedAt)
      // Utiliser le timestamp actuel pour être sûr que le tri fonctionne
      const now = new Date().toISOString();
      
      setConversations(prev => {
        const updated = prev.map(c => {
          if (c.id === message.conversationId) {
            const newUnread = isActiveConversation ? 0 : (c.unreadCount || 0) + 1;
            console.log('[Socket] Mise à jour conversation:', c.id, 'unread:', newUnread);
            return {
              ...c,
              lastMessage: {
                text: message.text,
                author: message.author,
                timestamp: now,
              },
              updatedAt: now,
              unreadCount: newUnread,
            };
          }
          return c;
        });
        console.log('[Socket] Conversations mises à jour, total:', updated.length);
        return updated;
      });
      
      if (preferencesRef.current.notifyPrivateMessages) {
        const truncated = message.text.length > 60
          ? message.text.slice(0, 57) + '…'
          : message.text;
        showToast({
          message: `${message.author}`,
          subtitle: truncated,
          type: 'chat',
          targetTab: 'chat',
        }, { sourceTab: 'chat' });
      }
    };

    // Écouter les nouvelles conversations (quand quelqu'un nous ajoute ou envoie le premier message)
    const handleNewConversation = (conversation: Conversation & { excludePseudo?: string }) => {
      // Ignorer si on est exclu (cas où on est l'auteur du premier message)
      if (conversation.excludePseudo?.toLowerCase() === session.pseudo.toLowerCase()) {
        console.log('[Socket] Nouvelle conversation ignorée (notre propre création)');
        return;
      }
      
      // Vérifier si on fait partie des participants
      const isParticipant = conversation.participants.some(
        p => p.toLowerCase() === session.pseudo.toLowerCase()
      );
      
      if (!isParticipant) return;
      
      setConversations(prev => {
        if (prev.some(c => c.id === conversation.id)) return prev;
        console.log(`[Socket] Nouvelle conversation ajoutée: ${conversation.name || conversation.id}`);
        return [conversation, ...prev];
      });
    };

    // Écouter les mises à jour de conversations (broadcast à tous)
    // Cet événement est émis quand quelqu'un envoie un message dans une conversation
    const handleConversationUpdated = (data: { 
      conversationId: string; 
      lastMessage: { text: string; author: string; timestamp: number | string } 
    }) => {
      console.log('[Socket] Conversation mise à jour:', data.conversationId, 'par', data.lastMessage.author);
      
      // Ignorer si c'est notre propre message
      if (data.lastMessage.author.toLowerCase() === session.pseudo.toLowerCase()) {
        return;
      }
      
      // Ignorer si c'est la conversation active (déjà géré par direct_message)
      if (activeConversation?.id === data.conversationId) {
        return;
      }
      
      const now = new Date().toISOString();
      
      setConversations(prev => {
        const updated = prev.map(c => {
          if (c.id === data.conversationId) {
            console.log('[Socket] Mise à jour via conversation_updated:', c.id);
            return {
              ...c,
              lastMessage: {
                text: data.lastMessage.text,
                author: data.lastMessage.author,
                timestamp: now,
              },
              updatedAt: now,
              unreadCount: (c.unreadCount || 0) + 1,
            };
          }
          return c;
        });
        return updated;
      });
    };

    // Écouter les mises à jour de paramètres de groupe
    const handleGroupSettingsUpdated = (data: {
      conversationId: string;
      name?: string;
      avatarEmoji?: string;
      avatarColor?: string;
    }) => {
      console.log('[Socket] Paramètres groupe mis à jour:', data.conversationId);
      
      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId) {
          return {
            ...c,
            name: data.name ?? c.name,
            avatarEmoji: data.avatarEmoji ?? c.avatarEmoji,
            avatarColor: data.avatarColor ?? c.avatarColor,
          };
        }
        return c;
      }));
      
      // Mettre à jour aussi la conversation active si c'est celle-ci
      setActiveConversation(prev => {
        if (prev?.id === data.conversationId) {
          return {
            ...prev,
            name: data.name ?? prev.name,
            avatarEmoji: data.avatarEmoji ?? prev.avatarEmoji,
            avatarColor: data.avatarColor ?? prev.avatarColor,
          };
        }
        return prev;
      });
    };

    // Écouter le retrait d'un membre
    const handleMemberRemoved = (data: { conversationId: string; pseudo: string }) => {
      console.log('[Socket] Membre retiré:', data.pseudo, 'de', data.conversationId);
      
      // Si c'est nous qui sommes retirés, supprimer la conversation
      if (data.pseudo.toLowerCase() === session.pseudo.toLowerCase()) {
        setConversations(prev => prev.filter(c => c.id !== data.conversationId));
        // Si on était dans cette conversation, revenir au chat global
        if (activeConversation?.id === data.conversationId) {
          setActiveConversation(null);
          setActiveView('global');
          setDirectMessages([]);
        }
        return;
      }
      
      // Sinon mettre à jour la liste des participants
      setConversations(prev => prev.map(c => {
        if (c.id === data.conversationId) {
          return {
            ...c,
            participants: c.participants.filter(p => p.toLowerCase() !== data.pseudo.toLowerCase()),
          };
        }
        return c;
      }));
      
      setActiveConversation(prev => {
        if (prev?.id === data.conversationId) {
          return {
            ...prev,
            participants: prev.participants.filter(p => p.toLowerCase() !== data.pseudo.toLowerCase()),
          };
        }
        return prev;
      });
    };

    // Écouter la suppression d'un groupe
    const handleGroupDeleted = (data: { conversationId: string }) => {
      console.log('[Socket] Groupe supprimé:', data.conversationId);
      
      setConversations(prev => prev.filter(c => c.id !== data.conversationId));
      
      // Si on était dans ce groupe, revenir au chat global
      if (activeConversation?.id === data.conversationId) {
        setActiveConversation(null);
        setActiveView('global');
        setDirectMessages([]);
      }
    };

    // Écouter l'accusé de lecture des messages (dans une conversation ouverte)
    const handleMessagesRead = (data: { 
      conversationId: string; 
      reader: string; 
      messageIds: string[];
      timestamp: number;
    }) => {
      // Ignorer si c'est nous qui avons lu (on a déjà mis à jour localement)
      if (data.reader.toLowerCase() === session.pseudo.toLowerCase()) {
        return;
      }
      
      console.log('[Socket] Messages lus par', data.reader, 'dans', data.conversationId, '-', data.messageIds.length, 'messages');
      
      // Mettre à jour le statut des messages dans la conversation active
      if (activeConversation?.id === data.conversationId) {
        setDirectMessages(prev => prev.map(msg => {
          // Mettre à jour uniquement nos propres messages qui ont été lus
          if (msg.author.toLowerCase() === session.pseudo.toLowerCase() && 
              data.messageIds.includes(msg.id)) {
            return { ...msg, status: 'read' as const };
          }
          return msg;
        }));
      }
    };
    
    // Écouter l'accusé de lecture global (pour la liste des conversations)
    const handleConversationMessagesRead = (data: {
      conversationId: string;
      reader: string;
      authors: string[];
      messageIds: string[];
      timestamp: number;
    }) => {
      // Lu depuis un autre panneau (ex. Mon équipe) → sync compteur local
      if (data.reader.toLowerCase() === session.pseudo.toLowerCase()) {
        localUnreadCountsRef.current.set(data.conversationId, 0);
        setConversations(prev => prev.map(c =>
          c.id === data.conversationId ? { ...c, unreadCount: 0 } : c
        ));
        return;
      }
      
      // Vérifier si nous sommes l'auteur des messages lus
      const weAreAuthor = data.authors.some(
        a => a.toLowerCase() === session.pseudo.toLowerCase()
      );
      
      if (!weAreAuthor) {
        return;
      }
      
      console.log('[Socket] Nos messages lus par', data.reader, 'dans conversation', data.conversationId);
      
      // Mettre à jour le statut du dernier message dans la liste des conversations
      setConversations(prev => prev.map(conv => {
        if (conv.id === data.conversationId && conv.lastMessage) {
          // Si le dernier message est de nous et fait partie des messages lus
          if (conv.lastMessage.author.toLowerCase() === session.pseudo.toLowerCase()) {
            return {
              ...conv,
              lastMessage: {
                ...conv.lastMessage,
                status: 'read' as const,
              },
            };
          }
        }
        return conv;
      }));
    };

    const handleDirectMessageReaction = (data: {
      conversationId?: string;
      messageId: string;
      reactions: MessageReaction[];
    }) => {
      if (!data.conversationId) return;
      setDirectMessages((prev) => prev.map((m) =>
        m.id === data.messageId ? { ...m, reactions: data.reactions } : m
      ));
    };

    const handleGameInviteUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        author: string;
        text: string;
        createdAt: string;
        status: string;
        messageType: 'game_invite';
        gameInvite: GameInvitePayload;
      };
    }) => {
      if (!activeConversation || data.conversationId !== activeConversation.id) return;
      setDirectMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              gameInvite: data.message.gameInvite,
              messageType: 'game_invite',
            }
          : m,
      ));
    };

    const handleLibraryShareUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        author: string;
        text: string;
        createdAt: string;
        status: string;
        messageType: 'library_share';
        libraryShare: LibrarySharePayload;
      };
    }) => {
      if (!activeConversation || data.conversationId !== activeConversation.id) return;
      setDirectMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              libraryShare: data.message.libraryShare,
              messageType: 'library_share',
            }
          : m,
      ));
    };

    const handleCiaAgendaInviteUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        author: string;
        text: string;
        createdAt: string;
        status: string;
        messageType: 'cia_agenda_invite';
        ciaAgendaInvite: CiaAgendaInvitePayload;
      };
    }) => {
      if (!activeConversation || data.conversationId !== activeConversation.id) return;
      setDirectMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              ciaAgendaInvite: data.message.ciaAgendaInvite,
              messageType: 'cia_agenda_invite',
            }
          : m,
      ));
    };

    socket.on('direct_message', handleDirectMessage);
    socket.on('game_invite_updated', handleGameInviteUpdated);
    socket.on('library_share_updated', handleLibraryShareUpdated);
    socket.on('cia_agenda_invite_updated', handleCiaAgendaInviteUpdated);
    socket.on('new_conversation', handleNewConversation);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('conversation_settings_updated', handleGroupSettingsUpdated);
    socket.on('member_removed', handleMemberRemoved);
    socket.on('group_deleted', handleGroupDeleted);
    socket.on('messages_read', handleMessagesRead);
    socket.on('conversation_messages_read', handleConversationMessagesRead);
    socket.on('message_reaction', handleDirectMessageReaction);
    
    return () => {
      socket.off('direct_message', handleDirectMessage);
      socket.off('game_invite_updated', handleGameInviteUpdated);
      socket.off('library_share_updated', handleLibraryShareUpdated);
      socket.off('cia_agenda_invite_updated', handleCiaAgendaInviteUpdated);
      socket.off('new_conversation', handleNewConversation);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('conversation_settings_updated', handleGroupSettingsUpdated);
      socket.off('member_removed', handleMemberRemoved);
      socket.off('group_deleted', handleGroupDeleted);
      socket.off('messages_read', handleMessagesRead);
      socket.off('conversation_messages_read', handleConversationMessagesRead);
      socket.off('message_reaction', handleDirectMessageReaction);
    };
  }, [socket, activeConversation, session.pseudo]);

  // Obtenir le nom d'affichage d'une conversation (texte brut)
  const getConversationDisplayName = (conv: Conversation): string => {
    if (conv.type === 'group') {
      // Pour les groupes inter-équipes, afficher "avec [autre équipe]"
      if (conv.isInterTeam && conv.displayName) {
        return conv.displayName;
      }
      if (conv.isInterTeam && conv.otherTeamName) {
        return `avec ${conv.otherTeamName}`;
      }
      return conv.name || 'Groupe sans nom';
    }
    // Pour les conversations directes, afficher l'autre participant
    const otherParticipant = conv.participants.find(
      p => p.toLowerCase() !== session.pseudo.toLowerCase()
    );
    return otherParticipant || 'Conversation';
  };

  // Obtenir le nom d'affichage avec "avec" estompé pour les groupes inter-équipes
  const { isCompact, isWide } = useResponsive();
  const enableMessageSwipe = !isWide;

  const renderConversationName = (conv: Conversation): React.ReactNode => {
    if (conv.isInterTeam && conv.otherTeamName) {
      return (
        <>
          <span className="inter-team-prefix">avec </span>
          {conv.otherTeamName}
        </>
      );
    }
    return getConversationDisplayName(conv);
  };

  const handleMobileBack = useCallback(() => {
    setShowMobileDetail(false);
  }, []);
  useTabReselect('chat', handleMobileBack);

  const detailTitleContent = (() => {
    if (activeView === 'beta') {
      return (
        <>
          <div className="avatar beta" style={{ width: 32, height: 32, fontSize: 16 }}>🧪</div>
          <div className="back-title-info">
            <span className="back-title-name">Salon bêta-testeurs</span>
            <span className="back-title-subtitle">
              {getGlobalChatParticipantsDisplay(betaConnectedUsers, session.pseudo)}
            </span>
          </div>
        </>
      );
    }
    if (activeView === 'global') {
      return (
        <>
          <div className="avatar global" style={{ width: 32, height: 32, fontSize: 16 }}>🌐</div>
          <div className="back-title-info">
            <span className="back-title-name">Chat Global {session.compet ? 'Compétition' : 'Détente'}</span>
            <span className="back-title-subtitle">
              {getGlobalChatParticipantsDisplay(connectedUsers, session.pseudo)}
            </span>
          </div>
        </>
      );
    }
    if (activeConversation) {
      const displayName = getConversationDisplayName(activeConversation);
      const subtitle = activeConversation.type === 'group'
        ? (activeConversation.isTeamGroup
          ? getTeamMembersDisplay(activeConversation.participants, session.pseudo)
          : getGroupParticipantsDisplay(activeConversation.participants, session.pseudo))
        : (session.compet && (() => {
            const otherPseudo = activeConversation.participants.find(
              p => p.toLowerCase() !== session.pseudo.toLowerCase()
            );
            return otherPseudo ? playerTeams[otherPseudo.toLowerCase()] : null;
          })()) || null;

      return (
        <>
          {activeConversation.type === 'direct' ? (
            <PlayerAvatar pseudo={displayName} size={32} className="avatar" />
          ) : activeConversation.avatarUrl ? (
            <div className="avatar group team-avatar" style={{ width: 32, height: 32 }}>
              <img
                src={activeConversation.avatarUrl}
                alt={activeConversation.name || 'Groupe'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            </div>
          ) : (
            <div
              className="avatar group"
              style={{ backgroundColor: activeConversation.avatarColor || '#128c7e', width: 32, height: 32, fontSize: 14 }}
            >
              {activeConversation.avatarEmoji || '👥'}
            </div>
          )}
          <div className="back-title-info">
            <span className="back-title-name">{displayName}</span>
            {subtitle && <span className="back-title-subtitle">{subtitle}</span>}
          </div>
        </>
      );
    }
    return null;
  })();

  const detailHeaderActions = (() => {
    if (activeView === 'direct' && activeConversation) {
      return (
        <>
          <button
            className="icon-button play-button"
            onClick={() => {
              const participants = activeConversation.participants.filter(
                p => p.toLowerCase() !== session.pseudo.toLowerCase() && !isBotPseudo(p)
              );
              setActiveTab('tables');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('create-draft-from-chat', {
                  detail: { participants }
                }));
              }, 100);
            }}
            title="Jouer avec les participants"
          >
            🎮
          </button>
          {activeConversation.type === 'group' && !activeConversation.isTeamGroup && (
            <button
              className="icon-button"
              onClick={() => setShowGroupSettings(true)}
              title="Paramètres du groupe"
            >
              ⚙️
            </button>
          )}
        </>
      );
    }
    return null;
  })();

  const sidebarContent = (
    <>
      <div className="sidebar-header">
        <h2 className="sidebar-title">Discussions</h2>
        <button
          className="new-conversation-button"
          onClick={() => setShowNewConversationModal(true)}
          title="Nouvelle discussion"
        >
          <NewChatIcon size={20} />
        </button>
      </div>

        <div className="conversations-list">
          {/* Chat Global - toujours en premier */}
          <div 
            className={`conversation-item ${!isCompact && activeView === 'global' ? 'active' : ''}`}
            onClick={selectGlobalChat}
          >
            <div className="conversation-avatar global"><GlobalChatIcon size={28} /></div>
            <div className="conversation-info">
              <div className="conversation-name">
                Chat Global {session.compet ? 'Compétition' : 'Détente'}
              </div>
              <div className="conversation-preview">
                {(() => {
                  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
                  if (!lastMsg) {
                    return `${connectedUsers.length} connectés`;
                  }
                  const isOwnMessage = lastMsg.pseudo.toLowerCase() === session.pseudo.toLowerCase();
                  const prefix = isOwnMessage ? 'Vous : ' : `${lastMsg.pseudo} : `;
                  const maxLength = 30;
                  const truncatedText = lastMsg.text.length > maxLength 
                    ? lastMsg.text.slice(0, maxLength) + '…' 
                    : lastMsg.text;
                  return prefix + truncatedText;
                })()}
              </div>
            </div>
          </div>

          {showBetaChat && (
            <div
              className={`conversation-item ${!isCompact && activeView === 'beta' ? 'active' : ''}`}
              onClick={selectBetaChat}
            >
              <div className="conversation-avatar beta">🧪</div>
              <div className="conversation-info">
                <div className="conversation-name">Salon bêta-testeurs</div>
                <div className="conversation-preview">
                  {(() => {
                    const lastMsg = betaMessages.length > 0 ? betaMessages[betaMessages.length - 1] : null;
                    if (!lastMsg) {
                      return betaConnectedUsers.length > 0
                        ? `${betaConnectedUsers.length} connecté(s)`
                        : 'Échanges entre bêta-testeurs Neo';
                    }
                    const isOwnMessage = lastMsg.pseudo.toLowerCase() === session.pseudo.toLowerCase();
                    const prefix = isOwnMessage ? 'Vous : ' : `${lastMsg.pseudo} : `;
                    const maxLength = 30;
                    const truncatedText = lastMsg.text.length > maxLength
                      ? lastMsg.text.slice(0, maxLength) + '…'
                      : lastMsg.text;
                    return prefix + truncatedText;
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Séparateur ou header archives */}
          {showArchivedConversations ? (
            <div className="archives-header sticky">
              <button 
                className="archives-button back"
                onClick={() => setShowArchivedConversations(false)}
              >
                ← Retour aux conversations actives
              </button>
            </div>
          ) : conversations.length > 0 && (
            <div className="conversations-divider">
              <span>Discussions privées</span>
            </div>
          )}

          {/* Liste des conversations privées (triées) */}
          {sortConversations(conversations).map(conv => (
            <div
              key={conv.id}
              className={`conversation-item ${!isCompact && activeConversation?.id === conv.id ? 'active' : ''} ${conv.pinned ? 'pinned' : ''} ${conv.isLegacy ? 'legacy' : ''}`}
              onClick={() => selectConversation(conv)}
            >
              {/* Indicateur épinglé */}
              {conv.pinned && <span className="pin-indicator">📌</span>}
              
              {/* Conversations legacy (messages du site webtarot.fr) */}
              {conv.isLegacy ? (
                <div className="conversation-avatar-wrapper">
                  <PlayerAvatar 
                    pseudo={conv.legacyCorrespondant || getConversationDisplayName(conv)} 
                    size={48}
                    className="conversation-avatar"
                  />
                  <span className="legacy-badge" title="Messages du site webtarot.fr">📨</span>
                </div>
              ) : conv.type === 'direct' ? (
                <PlayerAvatar 
                  pseudo={getConversationDisplayName(conv)} 
                  size={48}
                  className="conversation-avatar"
                />
              ) : conv.avatarUrl ? (
                <div 
                  className={`conversation-avatar group ${conv.isTeamGroup ? 'team-group' : ''} ${conv.isInterTeam ? 'inter-team' : ''}`}
                  title={conv.isTeamGroup ? 'Groupe d\'équipe' : conv.isInterTeam ? 'Groupe inter-équipes' : conv.name}
                >
                  <img 
                    src={conv.avatarUrl} 
                    alt={conv.name || 'Groupe'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                    onError={(e) => {
                      // Fallback vers emoji si l'image ne charge pas
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  {conv.isInterTeam && <span className="inter-team-badge">⚔️</span>}
                </div>
              ) : (
                <div 
                  className={`conversation-avatar group ${conv.isTeamGroup ? 'team-group' : ''} ${conv.isInterTeam ? 'inter-team' : ''}`}
                  style={{ backgroundColor: conv.avatarColor || '#128c7e' }}
                  title={conv.isTeamGroup ? 'Groupe d\'équipe' : conv.isInterTeam ? 'Groupe inter-équipes' : undefined}
                >
                  {conv.avatarEmoji || '👥'}
                  {conv.isInterTeam && <span className="inter-team-badge">⚔️</span>}
                </div>
              )}
              <div className="conversation-info">
                <div className="conversation-name">
                  {renderConversationName(conv)}
                  {/* Indicateur de membres connectés pour le groupe d'équipe */}
                  {conv.isTeamGroup && (() => {
                    const connectedTeammates = getConnectedTeammates(
                      conv.participants, 
                      session.pseudo, 
                      connectedUsers
                    );
                    if (connectedTeammates.length === 0) return null;
                    return (
                      <span 
                        className="team-online-indicator" 
                        title={`${connectedTeammates.length} coéquipier${connectedTeammates.length > 1 ? 's' : ''} en ligne : ${connectedTeammates.join(', ')}`}
                      >
                        {connectedTeammates.slice(0, 5).map((_, idx) => (
                          <span key={idx} className="online-dot" />
                        ))}
                        {connectedTeammates.length > 5 && (
                          <span className="online-count">+{connectedTeammates.length - 5}</span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                <div className="conversation-preview">
                  {conv.lastMessage ? (
                    <>
                      {conv.lastMessage.author.toLowerCase() === session.pseudo.toLowerCase() && (
                        <span className="message-status-icon">
                          <MessageStatusIcon status={conv.lastMessage.status} size={10} />
                        </span>
                      )}
                      <span className="preview-author">
                        {conv.lastMessage.author.toLowerCase() === session.pseudo.toLowerCase() 
                          ? 'Vous: ' 
                          : conv.type === 'group' 
                            ? `${conv.lastMessage.author}: ` 
                            : ''
                        }
                      </span>
                      {conv.lastMessage.text.length > 30 
                        ? conv.lastMessage.text.substring(0, 30) + '...'
                        : conv.lastMessage.text
                      }
                    </>
                  ) : (
                    <span className="no-messages">Pas de message</span>
                  )}
                </div>
              </div>
              <div className="conversation-meta">
                <span className="conversation-time">
                  {conv.lastMessage ? formatConversationDate(conv.lastMessage.timestamp) : ''}
                </span>
                {conv.unreadCount > 0 && (
                  <span className="unread-badge">{conv.unreadCount}</span>
                )}
                <button 
                  className={`pin-button ${conv.pinned ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePinConversation(conv.id);
                  }}
                  title={conv.pinned ? 'Désépingler' : 'Épingler'}
                >
                  📌
                </button>
              </div>
            </div>
          ))}

          {/* Message si aucune conversation */}
          {conversations.length === 0 && !showArchivedConversations && (
            <div className="conversations-placeholder">
              <p>💬 Discussions privées</p>
              <p className="small">
                Cliquez sur ✏️ pour démarrer une conversation
              </p>
            </div>
          )}
          
          {conversations.length === 0 && showArchivedConversations && (
            <div className="conversations-placeholder">
              <p>📦 Aucune conversation archivée</p>
            </div>
          )}

          {/* Bouton vers Archives (en bas, seulement si pas en mode archives) */}
          {!showArchivedConversations && archivedCount > 0 && (
            <div className="archives-button-container">
              <button 
                className="archives-button"
                onClick={() => setShowArchivedConversations(true)}
              >
                📦 Conversations archivées ({archivedCount})
              </button>
            </div>
          )}
        </div>
    </>
  );

  const detailContent = (
    <main className="chat-main">
        {/* Header du chat */}
        <header className="chat-header">
          <div className="chat-header-info">
            {activeView === 'beta' ? (
              <>
                <div className="avatar beta">🧪</div>
                <div className="chat-header-text">
                  <h2>Salon bêta-testeurs</h2>
                  <span className="chat-subtitle">
                    {getGlobalChatParticipantsDisplay(salonConnectedUsers, session.pseudo)}
                  </span>
                </div>
              </>
            ) : activeView === 'global' ? (
              <>
                <div className="avatar global">🌐</div>
                <div className="chat-header-text">
                  <h2>Chat Global {session.compet ? 'Compétition' : 'Détente'}</h2>
                  <span className="chat-subtitle">
                    {getGlobalChatParticipantsDisplay(connectedUsers, session.pseudo)}
                  </span>
                </div>
              </>
            ) : activeConversation ? (
              <div 
                className={`chat-header-group ${activeConversation.type === 'group' && !activeConversation.isTeamGroup ? 'clickable' : ''}`}
                onClick={() => activeConversation.type === 'group' && !activeConversation.isTeamGroup && setShowGroupSettings(true)}
                title={activeConversation.isTeamGroup ? 'Groupe d\'équipe (géré automatiquement)' : activeConversation.type === 'group' ? 'Cliquer pour modifier les paramètres du groupe' : undefined}
              >
                {activeConversation.type === 'direct' ? (
                  <PlayerAvatar 
                    pseudo={getConversationDisplayName(activeConversation)} 
                    size={44}
                    className="avatar"
                  />
                ) : activeConversation.avatarUrl ? (
                  <div className="avatar group team-avatar">
                    <img 
                      src={activeConversation.avatarUrl}
                      alt={activeConversation.name || 'Groupe'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                    />
                  </div>
                ) : (
                  <div 
                    className="avatar group"
                    style={{ backgroundColor: activeConversation.avatarColor || '#128c7e' }}
                  >
                    {activeConversation.avatarEmoji || '👥'}
                  </div>
                )}
                <div className="chat-header-text">
                  <h2>{getConversationDisplayName(activeConversation)}</h2>
                  <span className="chat-subtitle">
                    {activeConversation.type === 'group'
                      ? (activeConversation.isTeamGroup 
                          ? getTeamMembersDisplay(activeConversation.participants, session.pseudo) 
                          : getGroupParticipantsDisplay(activeConversation.participants, session.pseudo))
                      : (session.compet && (() => {
                          const otherPseudo = activeConversation.participants.find(
                            p => p.toLowerCase() !== session.pseudo.toLowerCase()
                          );
                          const team = otherPseudo ? playerTeams[otherPseudo.toLowerCase()] : null;
                          return team || null;
                        })()) || null
                    }
                  </span>
                </div>
                {activeConversation.type === 'group' && !activeConversation.isTeamGroup && (
                  <span className="settings-hint">⚙️</span>
                )}
              </div>
            ) : null}
          </div>
          <div className="chat-header-actions">
            {isGlobalSalonView && (
              <button
                className="icon-button"
                onClick={() => setShowUsers(!showUsers)}
                title="Afficher/masquer les utilisateurs"
              >
                👥
              </button>
            )}
            {activeView === 'direct' && activeConversation && (
              <button
                className="icon-button play-button"
                onClick={() => {
                  // Créer une table brouillon avec les participants de la conversation
                  const participants = activeConversation.participants.filter(
                    p => p.toLowerCase() !== session.pseudo.toLowerCase() && !isBotPseudo(p)
                  );
                  // Basculer vers l'onglet Tables et créer un draft
                  setActiveTab('tables');
                  // Émettre un événement custom que Tables.tsx écoutera
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('create-draft-from-chat', {
                      detail: { participants }
                    }));
                  }, 100);
                }}
                title="Jouer avec les participants de cette conversation"
              >
                🎮
              </button>
            )}
          </div>
        </header>

        {/* Zone des messages */}
        <div className="messages-container" ref={messagesContainerRef}>
          {isGlobalSalonView ? (
            <GlobalChatFeed
              messages={salonMessages}
              currentPseudo={session.pseudo}
              reactions={salonReactions}
              onToggleReaction={toggleGlobalReaction}
              onReply={handleStartReply}
              onReport={reportGlobalMessage}
              canModerate={canModerate || isAdmin}
              onModerationDone={(msg) => showToast({ message: msg, type: 'info' })}
              onRespondToGameInvite={respondToGlobalGameInvite}
              onCancelGameInvite={cancelGlobalGameInvite}
              onAccessDraft={navigateToDraft}
              onScrollToQuote={scrollToQuotedMessage}
              onOpenLibrary={navigateToLibrary}
              onOpenDiffere={navigateToDiffere}
              enableMessageShell
              enableSwipe={enableMessageSwipe}
            />
          ) : (
            // Messages de la conversation privée
            directMessages.length === 0 ? (
              <div className="messages-empty">
                <p>💬</p>
                <p>Démarrez la conversation !</p>
                <p className="small">Envoyez votre premier message</p>
              </div>
            ) : (
              <>
                {/* Header pour les conversations legacy */}
                {activeConversation?.isLegacy && (
                  <div className="legacy-message-header">
                    📨 Messages du site webtarot.fr
                  </div>
                )}
              {directMessages.map((msg, index) => {
                // Pour les groupes inter-équipes : alignement par équipe
                const isInterTeam = activeConversation?.isInterTeam === true;
                
                // Déterminer les membres de notre équipe
                let userTeamMembers: string[] = [];
                if (isInterTeam && activeConversation) {
                  const isUserInTeamA = activeConversation.userTeamId === activeConversation.teamAId;
                  userTeamMembers = isUserInTeamA 
                    ? (activeConversation.teamAMembers || [])
                    : (activeConversation.teamBMembers || []);
                }
                
                // Distinguer 3 cas pour les groupes inter-équipes :
                // 1. isOwnMessage : message de l'utilisateur → fond vert, à droite
                // 2. isFromOurTeam : message d'un coéquipier → fond gris, à droite avec avatar
                // 3. Autre équipe → fond gris, à gauche avec avatar
                const isOwnMessage = msg.author.toLowerCase() === session.pseudo.toLowerCase();
                const isFromOurTeam = isInterTeam && !isOwnMessage && userTeamMembers.length > 0 && userTeamMembers.some(
                  m => m.toLowerCase() === msg.author.toLowerCase()
                );
                
                // Pour l'alignement : à droite si c'est nous OU un coéquipier (inter-team)
                const alignRight = isInterTeam ? (isOwnMessage || isFromOurTeam) : isOwnMessage;
                
                // Afficher l'auteur : toujours pour les groupes inter-équipes, sinon seulement pour les messages entrants en groupe
                const showAuthor = isInterTeam
                  ? (index === 0 || directMessages[index - 1].author !== msg.author)
                  : (!isOwnMessage && activeConversation?.type === 'group' && 
                     (index === 0 || directMessages[index - 1].author !== msg.author));

                // Message legacy (du site webtarot.fr)
                const isLegacyMessage = activeConversation?.isLegacy || (msg as { isLegacy?: boolean }).isLegacy;

                // Classes CSS pour le message
                let messageClasses = 'message message-interactive';
                if (isLegacyMessage) {
                  messageClasses += ' legacy';
                }
                if (isInterTeam) {
                  messageClasses += ' inter-team';
                  if (isOwnMessage) {
                    messageClasses += ' own'; // Fond vert pour nos messages
                  } else if (isFromOurTeam) {
                    messageClasses += ' teammate'; // Coéquipier : fond gris, aligné à droite
                  } else {
                    messageClasses += ' opponent'; // Autre équipe : fond gris, aligné à gauche
                  }
                } else {
                  messageClasses += isOwnMessage ? ' outgoing' : ' incoming';
                }

                // Séparateur de date : afficher si c'est le premier message ou si la date change
                const currentDateKey = getDateKey(msg.createdAt);
                const previousDateKey = index > 0 ? getDateKey(directMessages[index - 1].createdAt) : null;
                const showDateSeparator = index === 0 || currentDateKey !== previousDateKey;
                const replyTarget: ReplyTarget = {
                  id: msg.id,
                  author: msg.author,
                  text: formatChatMessagePreview(msg.text),
                };
                const deepLinkPresentation = parseChatDeepLinks(msg.text);
                const hasDeepLinkCards = deepLinkPresentation.cards.length > 0;
                const deepLinkCardsOnly = hasDeepLinkCards && !deepLinkPresentation.displayText;

                const messageContent = (
                  <div className={messageClasses}>
                    {!alignRight && showAuthor && (
                      <PlayerAvatar pseudo={msg.author} size={32} className="message-avatar" />
                    )}
                    {!alignRight && !showAuthor && activeConversation?.type === 'group' && (
                      <div className="message-avatar-placeholder" />
                    )}

                    <div className="message-bubble-wrap">
                      <div className={`message-bubble ${
                        msg.messageType === 'game_invite'
                        || msg.messageType === 'library_share'
                        || msg.messageType === 'cia_agenda_invite'
                        || deepLinkCardsOnly
                          ? 'game-invite-bubble'
                          : ''
                      }`}>
                        {showAuthor
                          && msg.messageType !== 'game_invite'
                          && msg.messageType !== 'library_share'
                          && msg.messageType !== 'cia_agenda_invite'
                          && (isInterTeam || (!isOwnMessage && activeConversation?.type === 'group')) && (
                          <div className={`message-author ${isOwnMessage ? 'own' : ''}`}>
                            <ClickablePseudo pseudo={msg.author}>{msg.author}</ClickablePseudo>
                          </div>
                        )}
                        {msg.messageType === 'game_invite' && msg.gameInvite ? (
                          <>
                            <GameInviteMessage
                              invite={msg.gameInvite}
                              author={msg.author}
                              currentPseudo={session.pseudo}
                              showProposer={activeConversation?.type !== 'direct'}
                              showCancelledRecipients={activeConversation?.type !== 'direct'}
                              onRespond={(accepted) => respondToGameInvite(msg.id, accepted)}
                              onCancel={async () => { await cancelGameInvite(msg.id); }}
                              onAccess={() => navigateToDraft(
                                msg.gameInvite!.draftId,
                                msg.gameInvite!.config.gameMode,
                              )}
                            />
                            <span className="message-meta game-invite-meta">
                              <span className="message-time">
                                {formatTime(new Date(msg.createdAt).getTime())}
                              </span>
                            </span>
                          </>
                        ) : msg.messageType === 'cia_agenda_invite' && msg.ciaAgendaInvite ? (
                          <>
                            <CiaAgendaInviteMessage
                              invite={msg.ciaAgendaInvite}
                              author={msg.author}
                              currentPseudo={session.pseudo}
                              onRespond={(accepted) => respondToCiaAgendaInvite(msg.ciaAgendaInvite!, accepted)}
                              onCancel={async () => { await cancelCiaAgendaInvite(msg.ciaAgendaInvite!); }}
                            />
                            <span className="message-meta game-invite-meta">
                              <span className="message-time">
                                {formatTime(new Date(msg.createdAt).getTime())}
                              </span>
                            </span>
                          </>
                        ) : msg.messageType === 'library_share' && msg.libraryShare ? (
                          <>
                            <LibraryShareMessage
                              share={msg.libraryShare}
                              author={msg.author}
                              showProposer={activeConversation?.type !== 'direct'}
                              canCancel={
                                !msg.libraryShare.cancelledAt
                                && msg.author.toLowerCase() === session.pseudo.toLowerCase()
                              }
                              onCancel={async () => { await cancelLibraryShare(msg.libraryShare!.sharedGameId); }}
                              onOpen={() => {
                                const isSharer = msg.author.toLowerCase() === session.pseudo.toLowerCase();
                                navigateToLibrary({
                                  section: isSharer ? 'my_shares' : 'shared_private',
                                  sharedGameId: msg.libraryShare!.sharedGameId,
                                  mode: msg.libraryShare!.isPoll ? 'poll' : 'replay',
                                });
                              }}
                            />
                            <span className="message-meta game-invite-meta">
                              <span className="message-time">
                                {formatTime(new Date(msg.createdAt).getTime())}
                              </span>
                            </span>
                          </>
                        ) : (
                          <>
                            {msg.quote && (
                              <MessageQuoteBlock
                                quote={msg.quote}
                                isOutgoing={isOwnMessage || isFromOurTeam}
                                onClick={() => scrollToQuotedMessage(msg.quote!.id)}
                              />
                            )}
                            <ChatMessageContent
                              text={msg.text}
                              onOpenLibrary={navigateToLibrary}
                              onOpenDiffere={navigateToDiffere}
                              meta={(
                                <>
                                  {isOwnMessage && (
                                    <span className="message-status">
                                      <MessageStatusIcon status={msg.status} size={11} />
                                    </span>
                                  )}
                                  <span className="message-time">
                                    {formatTime(new Date(msg.createdAt).getTime())}
                                  </span>
                                </>
                              )}
                            />
                          </>
                        )}
                      </div>
                      {msg.messageType !== 'game_invite'
                        && msg.messageType !== 'library_share'
                        && msg.messageType !== 'cia_agenda_invite' && (
                        <MessageReactions
                          reactions={msg.reactions ?? []}
                          currentPseudo={session.pseudo}
                          onReact={(emoji) => toggleDirectReaction(msg.id, emoji)}
                        />
                      )}
                    </div>

                    {isInterTeam && (isOwnMessage || isFromOurTeam) && showAuthor && (
                      <PlayerAvatar pseudo={msg.author} size={32} className="message-avatar right" />
                    )}
                    {isInterTeam && (isOwnMessage || isFromOurTeam) && !showAuthor && (
                      <div className="message-avatar-placeholder right" />
                    )}
                  </div>
                );

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSeparator && (
                      <div className="date-separator sticky">
                        <span className="date-separator-text">{formatDateSeparator(msg.createdAt)}</span>
                      </div>
                    )}
                    {isLegacyMessage || msg.messageType === 'game_invite' ? (
                      messageContent
                    ) : (
                      <ChatMessageShell
                        messageId={msg.id}
                        alignRight={alignRight}
                        onReply={handleStartReply}
                        onReact={(emoji) => toggleDirectReaction(msg.id, emoji)}
                        replyTarget={replyTarget}
                        enableSwipe={enableMessageSwipe}
                      >
                        {messageContent}
                      </ChatMessageShell>
                    )}
                  </React.Fragment>
                );
              })}
              </>
            )
          )}
          
          {/* Indicateur de frappe */}
          {activeView === 'direct' && typingUsers.length > 0 && (
            <div className="typing-indicator">
              {/* Avatars seulement pour les groupes, pas pour les conversations directes */}
              {activeConversation?.type === 'group' && (
                <div className="typing-avatars">
                  {typingUsers.slice(0, 3).map((pseudo) => (
                    <PlayerAvatar key={pseudo} pseudo={pseudo} size={28} className="typing-avatar" />
                  ))}
                </div>
              )}
              <div className="typing-bubble">
                <div className="typing-dots">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <ChatInputComposer
          value={inputText}
          onChange={handleInputChange}
          onSend={handleInputSend}
          onKeyDown={handleInputKeyDown}
          placeholder={replyingTo ? 'Votre réponse…' : 'Tapez un message...'}
          disabled={!connected}
          replyingTo={replyingTo}
          onDismissReply={() => setReplyingTo(null)}
          insertEmoji={insertEmoji}
          inputRef={inputRef}
          showAttachMenu={connected}
          onPoll={handleAttachPoll}
          onGameInvite={handleAttachGameInvite}
          gameInviteTitle={gameInviteMeta.title}
          gameInviteSubtitle={canUseAttachMenu
            ? gameInviteMeta.subtitle
            : 'Disponible dans le salon global ou une conversation'}
          gameInviteDisabled={!canUseAttachMenu}
          onCiaRdv={ciaRdvAttachMeta ? handleAttachCiaRdv : undefined}
          ciaRdvTitle={ciaRdvAttachMeta?.title}
          ciaRdvSubtitle={ciaRdvAttachMeta?.subtitle}
        />
      </main>
  );

  return (
    <div className="chat-container">
      <MasterDetail
        master={sidebarContent}
        detail={detailContent}
        detailTitle={detailTitleContent}
        detailHeaderRight={detailHeaderActions}
        hasSelection={showMobileDetail}
        onBack={handleMobileBack}
      />

      {/* Panneau des utilisateurs connectés (wide uniquement, géré par responsive.css) */}
      {showUsers && !isCompact && isGlobalSalonView && (
        <aside className="users-sidebar">
          <div className="users-header">
            <h3>En ligne ({salonConnectedUsers.length})</h3>
          </div>
          <div className="users-list">
            {salonConnectedUsers.map((user) => {
              const team = playerTeams[user.pseudo.toLowerCase()];
              return (
                <div key={user.pseudo} className="user-item">
                  <PlayerAvatar pseudo={user.pseudo} size={32} />
                  <div className="user-item-info">
                    <ClickablePseudo pseudo={user.pseudo} className="user-item-name">{user.pseudoDisplay}</ClickablePseudo>
                    {session.compet && team && (
                      <span className="user-item-team">{team}</span>
                    )}
                  </div>
                  {activeView !== 'beta' && (
                    <span className={`user-source ${user.source}`}>
                      {user.source === 'app' ? 'App' : 'IRC'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      )}

      {/* Panneau développeur */}
      {showDevPanel && simulationStatus?.available && (
        <div className="dev-panel">
          <div className="dev-panel-header">
            <h3>🛠️ Mode Développeur</h3>
            <button className="close-button" onClick={() => setShowDevPanel(false)}>×</button>
          </div>
          
          <div className="dev-panel-content">
            {/* Mode Dieu */}
            <div className="dev-section">
              <h4>🔱 Mode Dieu</h4>
              <p className={`status ${simulationStatus.godMode ? 'active' : 'inactive'}`}>
                {simulationStatus.godMode 
                  ? '✓ Activé (connexion sans vérification)' 
                  : '✗ Désactivé'}
              </p>
              {!simulationStatus.godMode && (
                <p className="hint">Activez DEV_GOD_MODE=true dans docker-compose.yml</p>
              )}
            </div>

            {/* Simulation */}
            <div className="dev-section">
              <h4>🎮 Simulation d'activité</h4>
              <div className="simulation-controls">
                <button 
                  className={`toggle-button ${simulationStatus.active ? 'active' : ''}`}
                  onClick={toggleSimulation}
                >
                  {simulationStatus.active ? '⏸️ Arrêter' : '▶️ Démarrer'}
                </button>
                <span className="interval-info">
                  Intervalle: {(simulationStatus.interval / 1000)}s
                </span>
              </div>
              <p className={`status ${simulationStatus.active ? 'active' : 'inactive'}`}>
                {simulationStatus.active 
                  ? '● Simulation en cours...' 
                  : '○ Simulation arrêtée'}
              </p>
            </div>

            {/* Reset */}
            <div className="dev-section">
              <h4>🗑️ Réinitialisation</h4>
              <button 
                className="danger-button"
                onClick={resetSimulation}
              >
                Effacer toutes les discussions
              </button>
              <p className="hint">
                Efface l'historique des messages dans Redis
              </p>
            </div>

            {/* Infos */}
            <div className="dev-section">
              <h4>ℹ️ Informations</h4>
              <ul className="info-list">
                <li>Backend: {BACKEND_URL}</li>
                <li>Room: {activeGlobalRoomId}</li>
                <li>Socket: {connected ? '🟢 Connecté' : '🔴 Déconnecté'}</li>
                <li>Messages: {messages.length}</li>
                <li>Conversations: {conversations.length}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouvelle conversation */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onCreateDirect={createDirectConversation}
        onCreateGroup={createGroupConversation}
        onCreateInterTeam={createInterTeamGroup}
        currentPseudo={session.pseudo}
        existingConversations={conversations}
        isCompet={session.compet}
        userTeamId={userTeamId}
      />

      {showOfflineInviteConfirm && (
        <GameInviteOfflineConfirm
          participantNames={offlineInviteParticipantNames}
          onConfirm={() => {
            setShowOfflineInviteConfirm(false);
            void sendGameInvite();
          }}
          onCancel={() => setShowOfflineInviteConfirm(false)}
        />
      )}

      {showExistingInviteConfirm && (
        <GameInviteExistingConfirm
          onReplace={() => { void handleReplaceExistingGameInvite(); }}
          onKeep={() => setShowExistingInviteConfirm(false)}
        />
      )}

      {showCiaRdvModal && ciaRdvOpponent && (
        <CiaRdvModal
          opponentDisplay={ciaRdvOpponent.display}
          onClose={() => setShowCiaRdvModal(false)}
          onSubmit={handleSubmitCiaRdvFromChat}
        />
      )}

      {/* Modal paramètres de groupe */}
      {showGroupSettings && activeConversation && activeConversation.type === 'group' && (
        <GroupSettingsModal
          conversation={{
            id: activeConversation.id,
            name: activeConversation.name || '',
            avatarEmoji: activeConversation.avatarEmoji || '👥',
            avatarColor: activeConversation.avatarColor || '#128c7e',
            participants: activeConversation.participants,
          }}
          sessionPseudo={session.pseudo}
          onClose={() => setShowGroupSettings(false)}
          onSave={updateGroupSettings}
          onAddMember={addGroupMember}
          onRemoveMember={removeGroupMember}
          onDeleteGroup={deleteGroup}
        />
      )}

      {/* Toast de déconnexion */}
      {!connected && (
        <div className="disconnected-toast">
          <DisconnectedIcon size={20} />
          <span>Connexion perdue - Reconnexion en cours...</span>
        </div>
      )}
    </div>
  );
}
